/* HC - IB Gift Choice UI (workstream A5) - 2026-07-21
 *
 * The shopper-facing renderer for the first-party gift engine's CHOICE rewards.
 * Pending choices open a true OVERLAY MODAL above everything, including the cart
 * drawer. Rebuilt 2026-07-21 to Noah's approved mockup (a "Choose your free
 * gift" picker): tiles SELECT, the CTA commits. One rule at a time — multiple
 * pending rules QUEUE (a small "OFFER 1 OF 2" marker orients the shopper), and
 * committing a rule plays a brief "Added to your order" beat before advancing.
 *
 * Pure engine consumer:
 *   Listens : document 'ibgift:state'          (desired state incl. choice slots)
 *             document 'ibgift:choices-updated' (optimistic nudge)
 *             document 'cart:updated'           (re-badge reward line items + toast)
 *   Calls   : window.IBGiftEngine.selectChoice(ruleId, variantId)  (append-only —
 *             one call per NEW pick; committed picks are never re-sent)
 *
 * Hard invariants:
 *   - NEVER touch the cart directly (no cart layer, no /cart AJAX). The engine's
 *     reconciler owns cart mutations; this UI only records the choice on commit.
 *   - NEVER make a network request. Option display data (title/image/size/value)
 *     must arrive in the manifest; absent it, render gracefully with what exists.
 *   - Fail silent to nothing: if the engine or a mount is absent, render nothing
 *     and throw nothing. Everything is behind settings.gift_engine_enabled.
 *
 * The render core (buildModel / buildModalView / renderModalView / toggleSelection
 * / commitSelection) is pure and exposed on window.IBGiftChoiceUI._test so it can
 * be unit-tested without a DOM.
 */
(function () {
  'use strict';


  /* Five rotating pastel radial gradients — the tile backdrop when an option
   * carries no product image. Verbatim from the approved mockup. */
  var GRADIENTS = [
    'radial-gradient(120% 90% at 50% 14%, rgba(255,255,255,0.7), #E3ECF2 68%, #D6E2EC 100%)',
    'radial-gradient(120% 90% at 50% 14%, rgba(255,255,255,0.7), #F5E2E4 68%, #EFD3D7 100%)',
    'radial-gradient(120% 90% at 50% 14%, rgba(255,255,255,0.7), #EAE4F0 68%, #DED4EC 100%)',
    'radial-gradient(120% 90% at 50% 14%, rgba(255,255,255,0.7), #E4EBDD 68%, #D6E1CB 100%)',
    'radial-gradient(120% 90% at 50% 14%, rgba(255,255,255,0.7), #F1EADE 68%, #E7DCCB 100%)'
  ];

  /* Inline hairline glyphs (currentColor) — no icon library. */
  var SVG = {
    /* the gift knot, used in the header medallion + drawer re-entry row */
    gift:
      '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" ' +
      'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<rect x="3" y="8" width="18" height="4" rx="1"></rect><path d="M12 8v13"></path>' +
      '<path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"></path>' +
      '<path d="M7.5 8a2.5 2.5 0 0 1 0-5C10 3 12 8 12 8s2-5 4.5-5a2.5 2.5 0 0 1 0 5"></path></svg>',
    /* the checkmark, used on selection dots + line-item badge */
    check:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<path d="M20 6 9 17l-5-5"></path></svg>',
    /* the close X */
    close:
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false">' +
      '<path d="M18 6 6 18M6 6l12 12"></path></svg>'
  };

  /* The bottle silhouette placeholder that sits over the pastel gradient when an
   * option has no image (verbatim from the mockup). */
  var BOTTLE_SVG =
    '<svg viewBox="0 0 60 88" width="46" height="67" fill="none" aria-hidden="true" ' +
    'focusable="false" style="display:block;position:relative;z-index:1;">' +
    '<ellipse cx="30" cy="83" rx="18" ry="3.4" fill="#1C1614" opacity="0.06"></ellipse>' +
    '<rect x="24" y="5" width="12" height="9" rx="2" fill="#EFE6D7" stroke="#E1D6C4" stroke-width="1"></rect>' +
    '<path d="M23 14 h14 v3 c5 1.5 7 5 7 11 v45 c0 6 -4 10 -10 10 h-8 c-6 0 -10 -4 -10 -10 v-45 c0 -6 2 -9.5 7 -11 z" ' +
    'fill="#FFFFFF" stroke="#E4DACB" stroke-width="1.3"></path>' +
    '<circle cx="22" cy="45" r="2" fill="none" stroke="#C88" stroke-width="1.1"></circle>' +
    '<circle cx="27.6" cy="45" r="2" fill="none" stroke="#C88" stroke-width="1.1"></circle>' +
    '<circle cx="22" cy="50.4" r="2" fill="none" stroke="#C88" stroke-width="1.1"></circle>' +
    '<circle cx="27.6" cy="50.4" r="2" fill="none" stroke="#C88" stroke-width="1.1"></circle>' +
    '<rect x="19" y="57" width="22" height="1.8" rx="0.9" fill="#D9D0C2"></rect>' +
    '<rect x="19" y="61" width="15" height="1.6" rx="0.8" fill="#E3DBCE"></rect></svg>';

  /* ------------------------------------------------------------------ *
   * Small utilities
   * ------------------------------------------------------------------ */

  function hasDoc() {
    return typeof document !== 'undefined' && document && document.addEventListener;
  }

  function engineApi() {
    return (typeof window !== 'undefined' && window.IBGiftEngine) || null;
  }

  /* Theme setting bridge: the loader snippet stamps this global from
   * settings.gift_show_value. Absent (preview/tests) -> value display defaults
   * on, but only ever surfaces when an option actually carries price data. */
  function readConfig() {
    var cfg = (typeof window !== 'undefined' && window.__IB_GIFT_CHOICE_CONFIG__) || {};
    return { showValue: cfg.showValue !== false };
  }

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* href/link URLs from manifest data: only same-origin relative or http(s).
   * Never javascript:/data:/vbscript: in a navigable link. */
  function safeUrl(value) {
    if (typeof value !== 'string' || !value) return '';
    var trimmed = value.replace(/^\s+/, '');
    if (/^https?:\/\//i.test(trimmed) || trimmed.charAt(0) === '/') return trimmed;
    return '';
  }

  /* Image src is more permissive than a link: a data:image/* URI renders a
   * picture and cannot execute script, so allow it (a manifest could inline a
   * tiny thumbnail). Still reject data:text/html and other actives. */
  function safeImageUrl(value) {
    if (typeof value !== 'string' || !value) return '';
    var trimmed = value.replace(/^\s+/, '');
    if (/^data:image\//i.test(trimmed)) return trimmed;
    return safeUrl(trimmed);
  }

  function num(value, fallback) {
    var n = Number(value);
    return isFinite(n) ? n : fallback;
  }

  /* A positive finite retail value, or null. The manifest omits value today;
   * it lights up the strikethrough + "VALUE" tag once options carry it. */
  function normValue(raw) {
    if (raw == null || raw === '') return null;
    var n = Number(raw);
    if (!isFinite(n) || n <= 0) return null;
    return n;
  }

  function money(n) { return '$' + Number(n).toFixed(2); }

  /* ------------------------------------------------------------------ *
   * Model — pure. Turns an ibgift:state detail into render-ready cards.
   * `prevRuleIds` is the set of choice ruleIds shown last render, so a rule
   * that vanished (threshold lost) can be surfaced once, quietly.
   * ------------------------------------------------------------------ */

  function normOption(raw) {
    raw = raw || {};
    var variantId = raw.variantId != null ? raw.variantId : raw.v;
    var url = safeUrl(raw.url);
    if (!url && typeof raw.handle === 'string' && raw.handle) {
      url = '/products/' + encodeURIComponent(raw.handle);
    }
    return {
      variantId: variantId != null ? String(variantId) : '',
      qty: num(raw.quantity != null ? raw.quantity : raw.qty, 1) || 1,
      title: (typeof raw.title === 'string' && raw.title) ? raw.title : '',
      brand: (typeof raw.brand === 'string' && raw.brand) ? raw.brand : '',
      /* size ("950 ml") and value are v-next display fields; absent today, the
       * card degrades cleanly (no size row, no value tag). */
      size: (typeof raw.size === 'string' && raw.size) ? raw.size : '',
      value: normValue(raw.value != null ? raw.value : raw.price),
      image: safeImageUrl(raw.image || raw.featured_image || raw.imageUrl),
      url: url,
      oos: raw.oos === true || raw.available === false
    };
  }

  function buildCard(choice) {
    var slots = num(choice.slots, 0) || 0;
    var options = [];
    var srcOptions = choice.options || [];
    for (var i = 0; i < srcOptions.length; i++) options.push(normOption(srcOptions[i]));

    var selectedVariantIds = [];
    var srcSelected = choice.selected || [];
    for (var s = 0; s < srcSelected.length; s++) {
      var norm = normOption(srcSelected[s]);
      if (norm.variantId) selectedVariantIds.push(norm.variantId);
    }

    var optionByVariant = {};
    for (var o = 0; o < options.length; o++) optionByVariant[options[o].variantId] = options[o];

    var chosenOos = [];
    var chosenNames = [];
    for (var c = 0; c < selectedVariantIds.length; c++) {
      var opt = optionByVariant[selectedVariantIds[c]];
      if (opt && opt.oos) chosenOos.push(selectedVariantIds[c]);
      if (opt && opt.title) chosenNames.push(opt.title);
    }

    var filledSlots = selectedVariantIds.length;
    var needsChoice = filledSlots < slots;

    return {
      kind: 'choice',
      ruleId: String(choice.ruleId),
      version: choice.version,
      slots: slots,
      options: options,
      selectedVariantIds: selectedVariantIds,
      needsChoice: needsChoice,
      sealed: !needsChoice && slots > 0,
      chosenOos: chosenOos,
      chosenNames: chosenNames
    };
  }

  /* HC - deal card model - 2026-09-01
   * Pure. A DEAL entry from ibgift:state -> a render-ready card:
   *   headline  "Your cart has 48 CODE+ shades and qualifies for the ..."
   *   price     the fixed price Discount Kit charges, with the regular struck
   *   paid/free the bundle's own contents, from the product page via manifest
   *   freeValue what the free items are worth (regular - price)
   *   state     'offered' (CTA live) | 'taken' (added, no slot left)
   * Superseded tiers produce no card — the engine already picked the tier. */
  function buildDealCard(entry) {
    entry = entry || {};
    if (entry.ruleId == null || entry.superseded) return null;
    var opt = normOption(entry.option || {});
    var deal = entry.deal || {};
    var price = normValue(deal.price);
    var regular = normValue(deal.regular);
    var freeValue = normValue(deal.freeValue);
    var paid = [];
    var free = [];
    var i;
    for (i = 0; i < (deal.paid || []).length; i++) {
      var pItem = deal.paid[i] || {};
      paid.push({ qty: num(pItem.qty, 1) || 1, title: String(pItem.title || pItem.sku || '') });
    }
    for (i = 0; i < (deal.free || []).length; i++) {
      var fItem = deal.free[i] || {};
      free.push({ qty: num(fItem.qty, 1) || 1, title: String(fItem.title || fItem.sku || '') });
    }
    var slots = num(entry.slots, 0) || 0;
    var inCart = num(entry.inCart, 0) || 0;
    var remaining = num(entry.remaining, Math.max(0, slots - inCart)) || 0;
    var amount = num(entry.amount, 0) || 0;
    var min = num(entry.min, 0) || 0;
    return {
      kind: 'deal',
      ruleId: String(entry.ruleId),
      version: entry.version,
      name: String(entry.name || opt.title || ''),
      variantId: opt.variantId,
      title: opt.title,
      image: opt.image,
      url: opt.url,
      oos: opt.oos,
      amount: amount,
      min: min,
      slots: slots,
      inCart: inCart,
      remaining: remaining,
      state: remaining > 0 ? 'offered' : 'taken',
      price: price,
      regular: regular,
      freeValue: freeValue,
      priceLabel: price != null ? money(price) : '',
      regularLabel: regular != null && price != null && regular > price ? money(regular) : '',
      freeValueLabel: freeValue != null ? money(freeValue) + ' value' : '',
      paid: paid,
      free: free
    };
  }

  function dealCardsOf(cards) {
    var out = [];
    for (var i = 0; i < (cards || []).length; i++) {
      if (cards[i].kind === 'deal') out.push(cards[i]);
    }
    return out;
  }

  function buildModel(detail, prevRuleIds) {
    detail = detail || {};
    prevRuleIds = prevRuleIds || {};
    var cards = [];
    var activeRuleIds = {};
    var choices = detail.choices || [];

    /* HC - modal is pending-choice-only (Noah, 2026-07-20) - 2026-07-20
     * A card exists ONLY while a rule still needs a pick. Completed choices and
     * auto-added single-option gifts get NO card: the line badge plus the theme
     * toast are the whole confirmation. Rules that are live-but-unrendered still
     * count as active so they never emit a phantom "offer removed" card. */
    for (var i = 0; i < choices.length; i++) {
      var choice = choices[i];
      if (!choice || choice.ruleId == null) continue;
      var card = buildCard(choice);
      if (card.slots <= 0) continue;
      activeRuleIds[card.ruleId] = true;
      /* A completed pick whose chosen option went OOS is NOT resolved — the
       * engine cannot insert it, so the picker must resurface for a re-pick. */
      if (!card.needsChoice && !card.chosenOos.length) continue;
      cards.push(card);
    }

    var reasons = detail.reasons || [];
    for (var ar = 0; ar < reasons.length; ar++) {
      if (reasons[ar] && reasons[ar].code === 'auto_single_option') {
        activeRuleIds[String(reasons[ar].ruleId)] = true;
      }
    }

    /* HC - deal cards (2026-09-01): a paid add-on the cart qualifies for.
     * One card per OFFERED deal (superseded tiers are hidden — the engine has
     * already chosen the highest met tier). A taken deal keeps a quiet
     * "added" card while it has no slot left, so the shopper sees that the
     * bundle they accepted is the one in the cart. A deal is never a pending
     * CHOICE (pendingCount ignores it), but an offered deal does enter the
     * auto-open signature (Noah 2026-09-02: the offer is a pop-up, the rail
     * card is the re-entry) — see pendingSignature / stagedDealCard. */
    var deals = detail.deals || [];
    for (var di = 0; di < deals.length; di++) {
      var dealCard = buildDealCard(deals[di]);
      if (!dealCard) continue;
      cards.push(dealCard);
    }

    /* Threshold lost: a rule shown last render that is no longer live at all. */
    for (var ruleId in prevRuleIds) {
      if (!Object.prototype.hasOwnProperty.call(prevRuleIds, ruleId)) continue;
      if (activeRuleIds[ruleId]) continue;
      cards.push({ kind: 'lost', ruleId: ruleId });
    }

    return { cards: cards, activeRuleIds: activeRuleIds };
  }

  function choiceCardsOf(cards) {
    var out = [];
    for (var i = 0; i < (cards || []).length; i++) {
      if (cards[i].kind === 'choice') out.push(cards[i]);
    }
    return out;
  }

  /* ------------------------------------------------------------------ *
   * Selection semantics — pure. The selection is a MULTISET (an array that may
   * hold the same variant more than once): the engine now stores one pick per
   * earned slot with duplicates legal (review F12 — a 36-tube cart earns two
   * Smoothies slots and the shopper may want the same emulsion twice).
   *
   *   Single-pick (maxPick <= 1): pure radio — re-clicking the chosen tile
   *     clears it, clicking another replaces it.
   *   Multi-pick: below the cap, a click adds one unit of that tile (so a second
   *     click on a chosen tile adds a second unit -> a "x2" chip). AT the cap, a
   *     click on a chosen tile steps it down one unit; a click on an un-chosen
   *     tile swaps out the oldest unit for it.
   * `maxPick` is the REMAINING slot count, never total — the single authority
   * for the cap (review F10).
   * ------------------------------------------------------------------ */

  function toggleSelection(sel, id, maxPick) {
    sel = (sel || []).map(String);
    id = String(id);

    if (maxPick <= 1) {
      /* single: re-click clears, otherwise replace */
      return (sel.length === 1 && sel[0] === id) ? [] : [id];
    }
    if (sel.length < maxPick) return sel.concat([id]); /* room: add a unit (dups ok) */

    /* at cap */
    var last = sel.lastIndexOf(id);
    if (last !== -1) return sel.slice(0, last).concat(sel.slice(last + 1)); /* step down a unit */
    return sel.slice(1).concat([id]);              /* swap the oldest unit out */
  }

  /* ------------------------------------------------------------------ *
   * Commit state machine — pure (review F2). A modal commit is not confirmed
   * until the engine's reconciler reports its cart mutation resolved via the
   * `ibgift:mutation-result` event; recording the choice is not the same as the
   * gift landing in the cart. Phases: idle -> adding -> (added | error). The
   * reducer is driven by fake events in tests.
   *
   *   state = { phase, ruleId, variantIds }
   *   events:
   *     { type:'commit',  ruleId, variantIds }
   *     { type:'result',  ok, addedVariantIds, failedRules:[{ruleId,variantId,reason}] }
   *     { type:'timeout' }   (no result within the deadline)
   *     { type:'retry' }     (shopper retries after an error)
   *     { type:'reset' }     (beat finished / modal closed)
   * ------------------------------------------------------------------ */

  function commitTransition(state, event) {
    state = state || { phase: 'idle', ruleId: null, variantIds: [] };
    var e = event || {};
    if (e.type === 'commit') {
      return { phase: 'adding', ruleId: String(e.ruleId), variantIds: (e.variantIds || []).map(String) };
    }
    if (e.type === 'retry') {
      if (state.phase !== 'error') return state;
      return { phase: 'adding', ruleId: state.ruleId, variantIds: state.variantIds };
    }
    if (e.type === 'result') {
      /* 'error' also accepts a matching ok — a transient failure the engine
       * self-retried lands a later result carrying OUR variant (team-lead
       * 2026-07-21). 'adding' fails on failedRules for our rule; 'error' does
       * not re-fail (it's already there and watching for recovery).
       *
       * A commit is 'added' ONLY when EVERY committed variant is acknowledged
       * added — a partial (some ok, some failed) must NOT beat as full success
       * (review R2-3). A partial that carries a failure for our rule drops to
       * 'error' so the unfulfilled remainder re-presents. */
      if (state.phase !== 'adding' && state.phase !== 'error') return state;
      var need = state.variantIds.length;
      var failed = false;
      var fr = e.failedRules || [];
      for (var i = 0; i < fr.length; i++) {
        if (fr[i] && String(fr[i].ruleId) === state.ruleId) failed = true;
      }
      var set = {};
      var av = e.addedVariantIds || [];
      for (var a = 0; a < av.length; a++) set[String(av[a])] = true;
      var allAdded = need > 0;
      for (var v = 0; v < state.variantIds.length; v++) {
        if (!set[state.variantIds[v]]) allAdded = false;
      }
      if (allAdded) return { phase: 'added', ruleId: state.ruleId, variantIds: state.variantIds };
      if (failed && state.phase === 'adding') return { phase: 'error', ruleId: state.ruleId, variantIds: state.variantIds };
      return state; /* partial-so-far, or a result for some other commit — keep waiting */
    }
    if (e.type === 'timeout') {
      if (state.phase !== 'adding') return state;
      return { phase: 'error', ruleId: state.ruleId, variantIds: state.variantIds };
    }
    if (e.type === 'reset') return { phase: 'idle', ruleId: null, variantIds: [] };
    return state;
  }

  /* ------------------------------------------------------------------ *
   * Modal view-model — pure. Everything the markup needs, precomputed, so the
   * renderer is a straight string build and the live controller can diff/update
   * without re-deriving. Returns null when there is nothing to show.
   *
   * input = {
   *   card,             the choice card currently on stage
   *   selectedIds,      working selection multiset (array of variantId strings,
   *                     duplicates allowed for multi-pick)
   *   markerPosition,   this rule's 1-based position in the session queue
   *   markerTotal,      number of rules in the session queue (marker shows if >1)
   *   commitPhase,      'idle' | 'adding' | 'added' | 'error' (async commit, F2)
   *   added,            legacy alias for commitPhase === 'added'
   *   isMobile, viewportW, showValue
   * }
   * ------------------------------------------------------------------ */

  function buildModalView(input) {
    input = input || {};
    var card = input.card;
    if (!card || card.kind !== 'choice') return null;

    /* DELTA presentation (Noah 2026-07-21): the modal counts only what is still
     * pickable. Committed picks already live in the cart (as FREE GIFT lines), so
     * they are subtracted from the earned slot count — the shopper never re-picks
     * what they already have. An OOS committed pick never counts as fulfilled, so
     * it reopens its slot for a re-pick rather than blocking one. */
    var totalSlots = num(card.slots, 1) || 1;
    var committedOos = (card.chosenOos && card.chosenOos.length) || 0;
    var committedPicks = Math.max(0,
      (card.selectedVariantIds ? card.selectedVariantIds.length : 0) - committedOos);
    var reopened = committedPicks > 0;
    var maxPick = Math.max(1, totalSlots - committedPicks);
    var isSingle = maxPick <= 1;
    var showValue = input.showValue !== false;
    var isMobile = !!input.isMobile;
    var vw = num(input.viewportW, 1200);

    /* Keep only real, in-stock selection UNITS (duplicates preserved for multi-
     * pick; an OOS tile can never be picked). Then cap at the REMAINING slots —
     * this sanitized list is the single authority the commit sends (review F10).*/
    var optByVariant = {};
    for (var o = 0; o < card.options.length; o++) optByVariant[card.options[o].variantId] = card.options[o];
    var selectedIds = [];
    var src = input.selectedIds || [];
    for (var s = 0; s < src.length; s++) {
      var vId = String(src[s]);
      var so = optByVariant[vId];
      if (so && !so.oos) selectedIds.push(vId);
    }
    if (isSingle && selectedIds.length > 1) selectedIds = selectedIds.slice(-1);
    if (selectedIds.length > maxPick) selectedIds = selectedIds.slice(-maxPick);

    var count = selectedIds.length;              /* total UNITS selected this session */
    var atCap = !isSingle && count >= maxPick;

    /* Per-variant unit counts (multiset), for the "x2" chip. */
    var unitCount = {};
    for (var u = 0; u < selectedIds.length; u++) {
      unitCount[selectedIds[u]] = (unitCount[selectedIds[u]] || 0) + 1;
    }

    /* Commit phase (review F2). 'idle' | 'adding' | 'added' | 'error'. The legacy
     * `added` boolean still maps to the 'added' beat for older callers/tests. */
    var phase = input.commitPhase || (input.added ? 'added' : 'idle');
    var isAdded = phase === 'added';
    var isAdding = phase === 'adding';
    var isError = phase === 'error';
    var busy = isAdding || isAdded; /* selection is frozen while a commit resolves */

    /* Cards. Each precomputes its own selected/count/dim/oos flags + value tag. */
    var cards = [];
    var valueTotal = 0;
    var allSelectedPriced = count > 0;
    for (var i = 0; i < card.options.length; i++) {
      var opt = card.options[i];
      var c = unitCount[opt.variantId] || 0;
      var selected = c > 0;
      /* At cap, an un-chosen tile is de-emphasized — clicking it swaps (still
       * interactive, review F12), not blocked. */
      var dim = !selected && !opt.oos && atCap;
      var hasValue = showValue && opt.value != null;
      if (selected) {
        if (opt.value != null) valueTotal += opt.value * c;
        else allSelectedPriced = false;
      }
      cards.push({
        variantId: opt.variantId,
        brand: opt.brand,
        name: opt.title || 'Free gift',
        size: opt.size,
        image: opt.image,
        gradient: GRADIENTS[i % GRADIENTS.length],
        selected: selected,
        count: c,
        oos: opt.oos,
        dim: dim,
        hasValue: hasValue,
        valueStrike: hasValue ? money(opt.value) : '',
        valueTag: hasValue ? ('$' + Math.round(opt.value) + ' value') : ''
      });
    }

    var hasValueTotal = showValue && count > 0 && allSelectedPriced;

    /* Footer summary + CTA vary by phase. */
    var summaryTop, summarySub, summaryTone = 'ink';
    var ctaLabel = 'Add to my order';
    var ctaEnabled = false;
    if (isAdded) {
      summaryTop = 'Added to your order ✓';
      summarySub = count + ' free gift' + (count === 1 ? '' : 's') + ' added';
      summaryTone = 'ok';
    } else if (isAdding) {
      summaryTop = 'Adding your gift' + (count === 1 ? '' : 's') + '…';
      summarySub = 'One moment';
      ctaLabel = 'Adding…';
    } else if (isError) {
      summaryTop = 'Couldn’t add — try again';
      summarySub = 'Your gift wasn’t added';
      summaryTone = 'error';
      ctaLabel = 'Try again';
      ctaEnabled = true;
    } else if (count === 0) {
      summaryTop = 'Select a gift to continue';
      summarySub = isSingle ? 'Choose one · all free' : 'Pick up to ' + maxPick + ' · all free';
    } else {
      summaryTop = count + ' free gift' + (count === 1 ? '' : 's') + ' selected';
      summarySub = hasValueTotal ? (money(valueTotal) + ' total value') : 'All complimentary';
      ctaEnabled = true;
    }

    /* Queue marker: the current rule's 1-based position among the session's rules
     * (review F11 — a PARTIAL commit keeps the same rule, so committed-pick math
     * can't drive this; the controller supplies the rule position directly). */
    var position = Math.max(1, num(input.markerPosition, 1));
    var total = Math.max(position, num(input.markerTotal, 1));

    /* Grid: 4/3/2 columns by width — 2-col floor on phones (Noah 2026-07-21:
       full-width mobile cards were too tall, too much scrolling). */
    var cols = vw >= 1080 ? 4 : vw >= 820 ? 3 : 2;
    var effCols = Math.min(cols, Math.max(cards.length, 1));
    var few = effCols < cols;

    /* Availability line. */
    var availStatus, availTone;
    if (atCap) { availStatus = 'Max reached — tap a gift to swap'; availTone = 'rouge'; }
    else { availStatus = 'All complimentary'; availTone = 'caramel'; }

    return {
      ruleId: card.ruleId,
      maxPick: maxPick,
      remaining: maxPick,
      committedPicks: committedPicks,
      reopened: reopened,
      isSingle: isSingle,
      isMobile: isMobile,
      phase: phase,
      added: isAdded,
      adding: isAdding,
      errored: isError,
      busy: busy,
      cards: cards,
      selectedIds: selectedIds,
      count: count,
      atCap: atCap,
      pct: maxPick ? Math.min(100, (count / maxPick) * 100) : 0,
      giftNoun: isSingle ? 'gift' : 'gifts',
      favNoun: isSingle ? 'favorite' : 'favorites',
      singlePrompt: count ? 'Your free gift is selected' : 'Choose one free gift below',
      total: card.options.length,
      availStatus: availStatus,
      availTone: availTone,
      hasValueTotal: hasValueTotal,
      valueTotal: valueTotal,
      summaryTop: summaryTop,
      summarySub: summarySub,
      summaryTone: summaryTone,
      ctaLabel: ctaLabel,
      ctaEnabled: ctaEnabled,
      marker: { show: total > 1, position: position, total: total, label: 'Offer ' + position + ' of ' + total },
      grid: {
        cols: effCols,
        few: few,
        template: 'repeat(' + effCols + ',' + (few ? 'minmax(0,280px)' : '1fr') + ')',
        justify: few ? 'center' : 'stretch'
      }
    };
  }

  /* ------------------------------------------------------------------ *
   * View — pure. view-model -> HTML string. No DOM required.
   * ------------------------------------------------------------------ */

  function cardHtml(c, isSingle) {
    var media = c.image
      ? '<img class="ibg-card-img" src="' + esc(c.image) + '" alt="' + esc(c.name) +
        '" loading="lazy" onerror="this.style.display=\'none\'">'
      : BOTTLE_SVG;

    var badge = c.oos
      ? '<span class="ibg-badge ibg-badge--oos">Sold out</span>'
      : '<span class="ibg-badge">Free</span>';

    /* Selection dot: single -> inner dot; multi -> a check, or a "xN" unit count
     * chip once the same gift is picked more than once (review F12). */
    var dotInner = isSingle
      ? '<span class="ibg-card-dot-inner"></span>'
      : '<span class="ibg-card-check">' + SVG.check + '</span>' +
        '<span class="ibg-card-count">' + (c.count >= 2 ? '×' + c.count : '') + '</span>';
    var dot = '<span class="ibg-card-dot" aria-hidden="true">' + dotInner + '</span>';

    var brand = c.brand ? '<div class="ibg-card-brand">' + esc(c.brand) + '</div>' : '';

    var foot = '';
    if (c.size || c.hasValue) {
      var sizeEl = c.size ? '<span class="ibg-card-size">' + esc(c.size) + '</span>' : '<span></span>';
      var valueEl = c.hasValue
        ? '<span class="ibg-card-value"><span class="ibg-card-value-strike">' + esc(c.valueStrike) +
          '</span><span class="ibg-card-value-tag">' + esc(c.valueTag) + '</span></span>'
        : '';
      foot = '<div class="ibg-card-foot">' + sizeEl + valueEl + '</div>';
    }

    var cls = 'ibg-card';
    if (c.selected) cls += ' is-selected';
    if (c.count >= 2) cls += ' is-dup';
    if (c.dim) cls += ' is-dim';
    if (c.oos) cls += ' is-oos';

    return '<button type="button" class="' + cls + '" data-ibg-tile="' + esc(c.variantId) + '"' +
        ' aria-pressed="' + (c.selected ? 'true' : 'false') + '"' + (c.oos ? ' disabled aria-disabled="true"' : '') + '>' +
        '<span class="ibg-card-media">' +
          '<span class="ibg-card-grad" style="background:' + esc(c.gradient) + '"></span>' +
          badge + media +
        '</span>' +
        dot + brand +
        '<span class="ibg-card-name">' + esc(c.name) + '</span>' +
        foot +
        '<span class="ibg-card-veil" aria-hidden="true"></span>' +
        '<span class="ibg-card-ring" aria-hidden="true"></span>' +
      '</button>';
  }

  function renderModalView(view) {
    if (!view) return '';
    if (view.kind === 'deal') return renderDealModalView(view);

    var panelCls = 'ibg-modal-panel' + (view.isSingle ? ' is-single' : ' is-multi') +
      (view.isMobile ? ' is-mobile' : '') + (view.added ? ' is-added' : '') +
      (view.adding ? ' is-adding' : '') + (view.errored ? ' is-error' : '') +
      (view.atCap ? ' is-atcap' : '');

    var grab = view.isMobile ? '<div class="ibg-grab" aria-hidden="true"></div>' : '';

    var marker = view.marker.show
      ? '<span class="ibg-offer-marker">' + esc(view.marker.label) + '</span>'
      : '';

    /* Header. */
    var head =
      '<div class="ibg-head">' +
        '<span class="ibg-medallion" aria-hidden="true">' + SVG.gift + '</span>' +
        '<div class="ibg-head-text">' +
          '<div class="ibg-eyebrow-row"><span class="ibg-eyebrow">Your free gift</span>' + marker + '</div>' +
          '<h2 class="ibg-head-title">Choose your <em>free</em> ' + esc(view.giftNoun) + '</h2>' +
          '<p class="ibg-head-sub">You’ve unlocked <strong>' + view.maxPick + '</strong>' +
            (view.reopened ? ' more' : '') + ' free ' + esc(view.giftNoun) +
            ' with this order — pick your ' + esc(view.favNoun) + ' below.</p>' +
        '</div>' +
        '<button type="button" class="ibg-close" data-ibg-close aria-label="Close">' + SVG.close + '</button>' +
      '</div>';

    /* Progress (multi) or single-prompt line. */
    var progress = view.isSingle
      ? '<div class="ibg-single-prompt">' + esc(view.singlePrompt) + '</div>'
      : '<div class="ibg-progress">' +
          '<div class="ibg-progress-track"><div class="ibg-progress-fill" style="width:' + view.pct + '%"></div></div>' +
          '<span class="ibg-progress-label">' + view.count + ' of ' + view.maxPick + ' chosen</span>' +
        '</div>';

    /* Availability row. */
    var avail =
      '<div class="ibg-avail">' +
        '<span class="ibg-avail-count">' + view.total + ' gift' + (view.total === 1 ? '' : 's') + ' available</span>' +
        '<span class="ibg-avail-status ibg-avail-status--' + view.availTone + '">' + esc(view.availStatus) + '</span>' +
      '</div>';

    /* Cards. */
    var cardsHtml = '';
    for (var i = 0; i < view.cards.length; i++) cardsHtml += cardHtml(view.cards[i], view.isSingle);
    var grid = '<div class="ibg-grid" style="grid-template-columns:' + esc(view.grid.template) +
      ';justify-content:' + esc(view.grid.justify) + '">' + cardsHtml + '</div>';

    var scroll =
      '<div class="ibg-scroll-wrap">' +
        '<div class="ibg-fade ibg-fade--top" aria-hidden="true"></div>' +
        '<div class="ibg-fade ibg-fade--bottom" aria-hidden="true"></div>' +
        '<div class="ibg-scroll">' + avail + grid + '</div>' +
      '</div>';

    /* Footer. The summary is a live region so the confirmed "Added ✓" (and any
     * "Couldn't add") is announced to screen readers (review F19); with the
     * async commit flow it only reads success AFTER the mutation resolves. */
    var foot =
      '<div class="ibg-foot">' +
        '<div class="ibg-summary ibg-summary--' + view.summaryTone + '" role="status" aria-live="polite">' +
          '<span class="ibg-summary-top">' + esc(view.summaryTop) + '</span>' +
          '<span class="ibg-summary-sub">' + esc(view.summarySub) + '</span>' +
        '</div>' +
        '<div class="ibg-cta-row">' +
          '<button type="button" class="ibg-cta' + (view.ctaEnabled ? '' : ' is-disabled') + '" data-ibg-commit' +
            (view.ctaEnabled ? '' : ' aria-disabled="true"') + (view.busy ? ' aria-busy="true"' : '') + '>' +
            '<span class="ibg-cta-spinner" aria-hidden="true"></span>' +
            '<span class="ibg-cta-check">' + SVG.check + '</span>' +
            '<span class="ibg-cta-label">' + esc(view.ctaLabel) + '</span>' +
          '</button>' +
        '</div>' +
      '</div>';

    var headerZone = '<div class="ibg-header-zone">' + head + progress + '</div>';

    return '' +
      '<div class="ibg-modal-backdrop" data-ibg-close></div>' +
      '<div class="ibg-modal" role="dialog" aria-modal="true" aria-label="Choose your free gift" tabindex="-1">' +
        '<div class="' + panelCls + '">' +
          '<div class="ibg-topbar" aria-hidden="true"></div>' +
          grab + headerZone + scroll + foot +
        '</div>' +
      '</div>';
  }

  /* Controller convenience: build the view then render it in one call. Kept so
   * the pure path (buildModalView + renderModalView) stays independently
   * testable while the live code has a single entry point. */
  function renderModalHTML(input) {
    return renderModalView(buildModalView(input));
  }

  /* ------------------------------------------------------------------ *
   * HC - deal pop-up view (2026-09-02) — pure.
   * The same modal shell as the picker (backdrop, panel, header zone,
   * scroll, footer) presenting ONE offered deal: bundle, paid + free
   * contents, price, and the accept CTA. `phase` mirrors the picker's
   * commit phases so the footer/CTA restyle through the same CSS states.
   * ------------------------------------------------------------------ */
  function buildDealModalView(input) {
    input = input || {};
    var card = input.card;
    if (!card) return null;
    var pending = input.pending || null;
    var phase = input.added ? 'added' : (pending === 'adding' ? 'adding' : (pending === 'error' ? 'error' : 'idle'));
    var isMobile = !!input.isMobile;
    var count = card.amount || 0;

    var summaryTop, summarySub, summaryTone = 'ink';
    var ctaLabel = card.inCart > 0 ? 'Add another bundle' : 'Add bundle to cart';
    var ctaEnabled = true;
    if (phase === 'added') {
      summaryTop = 'Added to your order ✓';
      summarySub = card.name + ' is in your cart';
      summaryTone = 'ok';
      ctaEnabled = false;
    } else if (phase === 'adding') {
      summaryTop = 'Adding your bundle…';
      summarySub = 'One moment';
      ctaLabel = 'Adding…';
      ctaEnabled = false;
    } else if (phase === 'error') {
      summaryTop = 'Couldn’t add — try again';
      summarySub = 'Nothing was added';
      summaryTone = 'error';
      ctaLabel = 'Try again';
    } else {
      summaryTop = card.priceLabel ? 'Bundle price ' + card.priceLabel : card.name;
      summarySub = (card.regularLabel ? card.regularLabel + ' regular' : '') +
        (card.regularLabel && card.freeValueLabel ? ' · ' : '') +
        (card.freeValueLabel ? card.freeValueLabel.replace(/ value$/, ' free') : '');
    }

    return {
      kind: 'deal',
      ruleId: card.ruleId,
      card: card,
      isMobile: isMobile,
      phase: phase,
      added: phase === 'added',
      adding: phase === 'adding',
      errored: phase === 'error',
      busy: phase === 'adding',
      headTitle: 'You’ve unlocked an <em>intro deal</em>',
      headSub: 'Your cart has <strong>' + esc(String(count)) + ' qualifying ' + (count === 1 ? 'item' : 'items') +
        '</strong> and qualifies for the <strong>' + esc(card.name) + '</strong>.',
      summaryTop: summaryTop,
      summarySub: summarySub,
      summaryTone: summaryTone,
      ctaLabel: ctaLabel,
      ctaEnabled: ctaEnabled
    };
  }

  function renderDealModalView(view) {
    if (!view) return '';
    var card = view.card;
    var panelCls = 'ibg-modal-panel is-deal' +
      (view.isMobile ? ' is-mobile' : '') + (view.added ? ' is-added' : '') +
      (view.adding ? ' is-adding' : '') + (view.errored ? ' is-error' : '');
    var grab = view.isMobile ? '<div class="ibg-grab" aria-hidden="true"></div>' : '';

    var head =
      '<div class="ibg-head">' +
        '<span class="ibg-medallion" aria-hidden="true">' + SVG.gift + '</span>' +
        '<div class="ibg-head-text">' +
          '<div class="ibg-eyebrow-row"><span class="ibg-eyebrow">Intro deal</span></div>' +
          '<h2 class="ibg-head-title">' + view.headTitle + '</h2>' +
          '<p class="ibg-head-sub">' + view.headSub + '</p>' +
        '</div>' +
        '<button type="button" class="ibg-close" data-ibg-close aria-label="Close">' + SVG.close + '</button>' +
      '</div>';

    var media = card.image
      ? '<img class="ibg-dealm-img" src="' + esc(card.image) + '" alt="" loading="lazy" onerror="this.style.display=\'none\'">'
      : '<span class="ibg-dealm-img ibg-dealm-img--empty" aria-hidden="true">' + SVG.gift + '</span>';
    var priceRow = card.priceLabel
      ? '<span class="ibg-dealm-price">' +
          (card.regularLabel ? '<s class="ibg-deal-regular">' + esc(card.regularLabel) + '</s> ' : '') +
          '<b>' + esc(card.priceLabel) + '</b></span>'
      : '';
    var list = function (items, cls) {
      if (!items.length) return '';
      var out = '<ul class="ibg-deal-list ' + cls + '">';
      for (var i = 0; i < items.length; i++) {
        out += '<li><span class="ibg-deal-qty">' + esc(String(items[i].qty)) + ' ×</span> ' + esc(items[i].title) + '</li>';
      }
      return out + '</ul>';
    };
    var paid = card.paid.length
      ? '<div class="ibg-dealm-col"><span class="ibg-eyebrow">Paid, in the bundle</span>' + list(card.paid, 'ibg-deal-list--paid') + '</div>'
      : '';
    var free = card.free.length
      ? '<div class="ibg-dealm-col"><span class="ibg-eyebrow-row"><span class="ibg-eyebrow">Free, in the bundle</span>' +
          (card.freeValueLabel ? '<span class="ibg-card-value-tag">' + esc(card.freeValueLabel) + '</span>' : '') +
          '</span>' + list(card.free, 'ibg-deal-list--free') + '</div>'
      : '';
    var body =
      '<div class="ibg-dealm">' +
        '<div class="ibg-dealm-hero">' + media +
          '<div class="ibg-dealm-main">' +
            '<span class="ibg-dealm-title">' + esc(card.title || card.name) + '</span>' +
            priceRow +
            (card.freeValueLabel ? '<span class="ibg-dealm-value">' + esc(card.freeValueLabel.replace(/ value$/, '')) + ' of free product included</span>' : '') +
          '</div>' +
        '</div>' +
        ((paid || free) ? '<div class="ibg-dealm-cols">' + paid + free + '</div>' : '') +
      '</div>';
    var scroll =
      '<div class="ibg-scroll-wrap">' +
        '<div class="ibg-fade ibg-fade--top" aria-hidden="true"></div>' +
        '<div class="ibg-fade ibg-fade--bottom" aria-hidden="true"></div>' +
        '<div class="ibg-scroll">' + body + '</div>' +
      '</div>';

    var foot =
      '<div class="ibg-foot">' +
        '<div class="ibg-summary ibg-summary--' + view.summaryTone + '" role="status" aria-live="polite">' +
          '<span class="ibg-summary-top">' + esc(view.summaryTop) + '</span>' +
          '<span class="ibg-summary-sub">' + esc(view.summarySub) + '</span>' +
        '</div>' +
        '<div class="ibg-cta-row">' +
          '<button type="button" class="ibg-cta' + (view.ctaEnabled ? '' : ' is-disabled') + '" data-ibg-deal-accept="' + esc(card.ruleId) + '"' +
            (view.ctaEnabled ? '' : ' aria-disabled="true"') + (view.busy ? ' aria-busy="true"' : '') + '>' +
            '<span class="ibg-cta-spinner" aria-hidden="true"></span>' +
            '<span class="ibg-cta-check">' + SVG.check + '</span>' +
            '<span class="ibg-cta-label">' + esc(view.ctaLabel) + '</span>' +
          '</button>' +
        '</div>' +
      '</div>';

    return '' +
      '<div class="ibg-modal-backdrop" data-ibg-close></div>' +
      '<div class="ibg-modal" role="dialog" aria-modal="true" aria-label="Intro deal" tabindex="-1">' +
        '<div class="' + panelCls + '">' +
          '<div class="ibg-topbar" aria-hidden="true"></div>' +
          grab + '<div class="ibg-header-zone">' + head + '</div>' + scroll + foot +
        '</div>' +
      '</div>';
  }

  /* ------------------------------------------------------------------ *
   * Drawer re-entry row + open-once logic (pure).
   * ------------------------------------------------------------------ */

  function lostCardHtml(card) {
    return '<div class="ibg-lost" data-ibg-rule="' + esc(card.ruleId) + '">' +
      '<span class="ibg-lost-seal" aria-hidden="true">' + SVG.gift + '</span>' +
      '<span class="ibg-lost-text">' +
        '<span class="ibg-eyebrow">Free gift</span>' +
        '<span class="ibg-lost-title">Free-gift offer removed — the qualifying items left your cart</span>' +
      '</span>' +
    '</div>';
  }

  /* One string per pending rule capturing WHAT is pending. A higher slot count
   * or a newly-OOS chosen option changes the signature, so those re-open a
   * previously dismissed modal; the same pending state never re-opens. */
  function pendingSignature(cards) {
    var sig = {};
    for (var i = 0; i < (cards || []).length; i++) {
      var card = cards[i];
      /* HC - deal pop-up (2026-09-02): an OFFERED deal signs as its slot count
       * plus the units already held, so the first offer opens once, a tier
       * switch (new rule id) opens again, and earning a second bundle (slots
       * 2) re-offers — while the same offer never re-opens after dismissal. */
      if (card.kind === 'deal') {
        if (card.state === 'offered' && !card.oos) sig[card.ruleId] = 'd' + card.slots + ':' + card.inCart;
        continue;
      }
      if (card.kind !== 'choice') continue;
      sig[card.ruleId] = 's' + card.slots +
        (card.chosenOos && card.chosenOos.length ? ':oos:' + card.chosenOos.join(',') : '');
    }
    return sig;
  }

  /* seen: ruleId -> last signature auto-shown. Returns whether the modal should
   * auto-open, WHICH rule ids changed, and the updated seen map (pure —
   * storage is the controller's). */
  function computeAutoOpen(cards, seen) {
    seen = seen || {};
    var sig = pendingSignature(cards);
    var shouldOpen = false;
    var changed = [];
    var next = {};
    /* Only rules with a live pending choice keep their seen entry. A rule whose
     * offer was resolved or dequalified is forgotten, so a FRESH episode
     * (re-qualifying later) auto-opens again instead of staying suppressed. */
    for (var ruleId in seen) {
      if (!Object.prototype.hasOwnProperty.call(seen, ruleId)) continue;
      if (sig[ruleId] !== undefined) next[ruleId] = seen[ruleId];
    }
    for (var id in sig) {
      if (!Object.prototype.hasOwnProperty.call(sig, id)) continue;
      if (next[id] !== sig[id]) {
        shouldOpen = true;
        changed.push(id);
        next[id] = sig[id];
      }
    }
    return { shouldOpen: shouldOpen, changed: changed, seen: next };
  }

  function pendingCount(cards) {
    return choiceCardsOf(cards).length;
  }

  /* Slim drawer row — the re-entry affordance while choices are pending, plus
   * any quiet "offer removed" notes. */
  function renderEntryRowHTML(cards) {
    if (!cards || !cards.length) return '';
    var html = '';
    var pending = pendingCount(cards);
    if (pending > 0) {
      var label = pending > 1
        ? 'Choose your ' + pending + ' free gifts'
        : 'Choose your free gift';
      html += '<button type="button" class="ibg-row" data-ibg-open>' +
        '<span class="ibg-row-seal">' + SVG.gift + '</span>' +
        '<span class="ibg-row-text"><span class="ibg-eyebrow">Your free gift</span>' +
        '<span class="ibg-row-title">' + esc(label) + '</span></span>' +
        '<span class="ibg-row-arrow" aria-hidden="true">&rsaquo;</span>' +
        '</button>';
    }
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].kind === 'lost') html += lostCardHtml(cards[i]);
      if (cards[i].kind === 'deal') html += dealCardHtml(cards[i], ctrl.dealPending[cards[i].ruleId] || null);
    }
    return html;
  }

  /* HC - deal card markup - 2026-09-01
   * Rendered in the rail (drawer + cart page) as the re-entry point; the
   * pop-up itself is renderDealModalView (2026-09-02). `pending`
   * is the accept-in-flight state for this rule: null | 'adding' | 'error'.
   * The CTA calls engine.acceptDeal; nothing here touches the cart. */
  function dealCardHtml(card, pending) {
    var media = card.image
      ? '<img class="ibg-deal-img" src="' + esc(card.image) + '" alt="" loading="lazy" onerror="this.style.display=\'none\'">'
      : '<span class="ibg-deal-img ibg-deal-img--empty" aria-hidden="true">' + SVG.gift + '</span>';

    var headline = 'Your cart has <strong>' + esc(String(card.amount)) + ' qualifying ' +
      (card.amount === 1 ? 'item' : 'items') + '</strong> and qualifies for the <strong>' +
      esc(card.name) + '</strong>';

    var priceRow = '';
    if (card.priceLabel) {
      priceRow = '<span class="ibg-deal-price">' +
        (card.regularLabel ? '<s class="ibg-deal-regular">' + esc(card.regularLabel) + '</s> ' : '') +
        '<b>' + esc(card.priceLabel) + '</b></span>';
    }

    var list = function (items, cls) {
      if (!items.length) return '';
      var out = '<ul class="ibg-deal-list ' + cls + '">';
      for (var i = 0; i < items.length; i++) {
        out += '<li><span class="ibg-deal-qty">' + esc(String(items[i].qty)) + ' ×</span> ' + esc(items[i].title) + '</li>';
      }
      return out + '</ul>';
    };
    var paid = card.paid.length
      ? '<div class="ibg-deal-section"><span class="ibg-eyebrow">Paid, in the bundle</span>' + list(card.paid, 'ibg-deal-list--paid') + '</div>'
      : '';
    var free = card.free.length
      ? '<div class="ibg-deal-section"><span class="ibg-eyebrow-row"><span class="ibg-eyebrow">Free, in the bundle</span>' +
        (card.freeValueLabel ? '<span class="ibg-card-value-tag">' + esc(card.freeValueLabel) + '</span>' : '') +
        '</span>' + list(card.free, 'ibg-deal-list--free') + '</div>'
      : '';

    var cta;
    if (card.state === 'taken') {
      cta = '<span class="ibg-deal-added">' + SVG.check + ' Added to your order</span>';
    } else if (card.oos) {
      cta = '<span class="ibg-deal-added ibg-deal-added--oos">Sold out</span>';
    } else if (pending === 'adding') {
      cta = '<button type="button" class="ibg-cta ibg-deal-cta is-disabled" disabled aria-disabled="true">Adding…</button>';
    } else if (pending === 'error') {
      cta = '<button type="button" class="ibg-cta ibg-deal-cta" data-ibg-deal-accept="' + esc(card.ruleId) + '">Try again</button>' +
        '<span class="ibg-deal-error">That did not go through. Nothing was added.</span>';
    } else {
      cta = '<button type="button" class="ibg-cta ibg-deal-cta" data-ibg-deal-accept="' + esc(card.ruleId) + '">' +
        (card.inCart > 0 ? 'Add another bundle' : 'Add bundle to cart') + '</button>';
    }

    /* The head row is the re-entry to the pop-up while the deal is offered
     * (2026-09-02): a button with the same chevron as the gift picker row. */
    var headInner =
      '<span class="ibg-row-seal" aria-hidden="true">' + SVG.gift + '</span>' +
      '<span class="ibg-row-text"><span class="ibg-eyebrow">Intro deal</span>' +
      '<span class="ibg-deal-headline">' + headline + '</span></span>';
    var head = card.state === 'offered' && !card.oos
      ? '<button type="button" class="ibg-deal-head ibg-deal-head--open" data-ibg-deal-open="' + esc(card.ruleId) + '" aria-label="View the ' + esc(card.name) + '">' +
          headInner + '<span class="ibg-row-arrow" aria-hidden="true">&rsaquo;</span></button>'
      : '<div class="ibg-deal-head">' + headInner + '</div>';

    return '<div class="ibg-deal" data-ibg-deal="' + esc(card.ruleId) + '">' +
      head +
      '<div class="ibg-deal-body">' +
        media +
        '<div class="ibg-deal-main">' +
          '<span class="ibg-deal-title">' + esc(card.title || card.name) + '</span>' +
          priceRow +
          '<details class="ibg-deal-details"><summary>What’s in the bundle</summary>' + paid + free + '</details>' +
        '</div>' +
      '</div>' +
      '<div class="ibg-deal-foot">' + cta + '</div>' +
    '</div>';
  }

  /* ------------------------------------------------------------------ *
   * Engine writes — the ONLY mutations this file performs.
   * ------------------------------------------------------------------ */

  function commitSelection(engine, ruleId, variantIds) {
    if (!engine || typeof engine.selectChoice !== 'function') return;
    /* Append-only (Noah 2026-07-21): committed picks already live in the engine
     * and the cart, and the engine now allows duplicate variants across slots —
     * so a clear-then-reselect (or re-sending a committed pick) would drop or
     * double-insert gifts on the next slot bump. Send exactly the new picks the
     * shopper made in this modal session. */
    var picks = [];
    for (var i = 0; i < variantIds.length; i++) {
      if (variantIds[i]) picks.push(variantIds[i]);
    }
    if (!picks.length) return;
    /* Batch commit so a multi-pick yields ONE mutation-result carrying the full
     * ack set (a per-pick selectChoice loop reconciles separately and would
     * split the acks across events, stalling the commit machine's all-added
     * check). Fall back to the loop for older engine builds. */
    if (typeof engine.selectChoices === 'function') {
      engine.selectChoices(ruleId, picks);
    } else {
      for (var j = 0; j < picks.length; j++) engine.selectChoice(ruleId, picks[j]);
    }
  }

  /* ------------------------------------------------------------------ *
   * Live controller (DOM). Everything below only runs in a browser.
   * ------------------------------------------------------------------ */

  var SEEN_STORAGE_KEY = 'ibg:choice-seen';
  var MODAL_ROOT_ID = 'ib-gift-choice-modal-root';
  var ADDED_BEAT_MS = 1200;  /* in-modal confirmation beat before advancing */
  var COMMIT_TIMEOUT_MS = 6000; /* a missing mutation-result can never wedge the modal (F2) */

  var ctrl = {
    modalRoot: null,
    modalBound: false,
    modalOpen: false,
    lastFocus: null,
    prevRuleIds: {},        /* choice ruleIds shown last render */
    lastCards: [],
    lastTargets: [],
    lastCart: null,         /* last AUTHORITATIVE cart snapshot (for an idempotent retry, R2-4) */
    lastRewardKeys: {},     /* reward cart-line keys from the last cart:updated (F14) */
    shellSig: null,         /* identity of the currently-mounted modal shell */
    selByRule: {},          /* ruleId -> working selection multiset (array, dups ok) */
    sessionRules: [],       /* ordered ruleIds this modal session — drives the marker */
    scrollLock: null,       /* preserved scroll position while the modal locks the body */
    /* Async commit state machine (F2): { phase, ruleId, variantIds, card, timer, beatTimer } */
    commit: { phase: 'idle', ruleId: null, variantIds: [], card: null, timer: null, beatTimer: null },
    suppressToastVariants: {}, /* variantIds committed via the modal — no toast */
    dealPending: {},        /* ruleId -> 'adding' | 'error' while an accept is in flight (2026-09-01) */
    lastDealKeys: {},       /* _ib_deal cart-line keys from the last authoritative cart */
    dealStage: null,        /* ruleId of the deal the open modal is (or will be, after the picker queue) presenting (2026-09-02) */
    dealStageCard: null,    /* last rendered staged card — held through an in-flight accept */
    dealBeat: null,         /* { ruleId, card, timer } — the in-modal "Added" beat after an accept */
    dealTimers: {},         /* ruleId -> watchdog timer for an accept that never settles */
    dealAttempt: {}         /* ruleId -> attempt counter; a settle or watchdog from an older attempt is ignored (review 2026-09-02) */
  };
  var DEAL_ACCEPT_TIMEOUT_MS = COMMIT_TIMEOUT_MS; /* same watchdog as the picker commit */

  function seenGet() {
    try {
      var raw = sessionStorage.getItem(SEEN_STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (err) { return {}; }
  }

  function seenSet(map) {
    try { sessionStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(map)); } catch (err) { /* noop */ }
  }

  function viewportW() {
    return (typeof window !== 'undefined' && window.innerWidth) || 1200;
  }

  /* Is this element in a currently-rendered, visible subtree? Walks ancestors for
   * display:none / visibility:hidden so the hidden header drawer on /cart is
   * never chosen (review F13). Robust to whatever the drawer uses to hide. */
  function isVisible(el) {
    if (!el || typeof window === 'undefined' || !window.getComputedStyle) return !!el;
    if (el.offsetParent === null) {
      var pos = window.getComputedStyle(el).position;
      if (pos !== 'fixed' && pos !== 'sticky') return false; /* an ancestor is display:none */
    }
    var node = el;
    while (node && node.nodeType === 1) {
      var cs = window.getComputedStyle(node);
      if (cs.display === 'none' || cs.visibility === 'hidden') return false;
      node = node.parentElement;
    }
    return true;
  }

  function onCartPage() {
    try {
      if (typeof location !== 'undefined' && /(^|\/)cart(\/|$|\?|#)/.test(location.pathname)) return true;
    } catch (e) { /* noop */ }
    var body = hasDoc() && document.body;
    return !!(body && body.className && /(^|\s)template-cart(\s|$)/.test(body.className));
  }

  /* Pick the mount for the surface the shopper is actually looking at: the cart
   * page's own anchor on /cart, the drawer's otherwise — then fall back to any
   * VISIBLE anchor, then a visible legacy container. Never mounts into a hidden
   * subtree (the /cart-drawer-invisible-row bug, review F13). */
  function resolveMount() {
    if (!hasDoc() || !document.querySelectorAll) return null;
    var preferSurface = onCartPage() ? 'page' : 'drawer';

    var mounts = document.querySelectorAll('[data-ib-gift-mount]');
    var visibleMount = null, preferredMount = null;
    for (var i = 0; i < mounts.length; i++) {
      if (!isVisible(mounts[i])) continue;
      if (!visibleMount) visibleMount = mounts[i];
      if (mounts[i].getAttribute && mounts[i].getAttribute('data-ib-gift-surface') === preferSurface) {
        preferredMount = mounts[i];
      }
    }
    if (preferredMount) return preferredMount;
    if (visibleMount) return visibleMount;

    /* Legacy anchors (themes without the explicit mounts), visible-first. */
    var scrollables = document.querySelectorAll('.cart__scrollable');
    for (var s = 0; s < scrollables.length; s++) {
      if (isVisible(scrollables[s])) return scrollables[s];
    }
    var items = document.querySelectorAll('.cart__items');
    for (var k = 0; k < items.length; k++) {
      if (isVisible(items[k]) && items[k].parentNode) return items[k].parentNode;
    }
    return null; /* nothing visible — a hidden row is worse than none */
  }

  /* Mount targets for the re-entry rail. With explicit anchors we render into
   * EVERY [data-ib-gift-mount] (both the drawer and the cart page), so the row
   * survives a surface switch — the drawer being hidden at dismissal no longer
   * strands the row in the wrong surface (review R2-10). Legacy themes fall back
   * to the single visible mount. */
  function rowMountTargets() {
    if (!hasDoc() || !document.querySelectorAll) return [];
    var mounts = document.querySelectorAll('[data-ib-gift-mount]');
    if (mounts.length) return [].slice.call(mounts);
    var legacy = resolveMount();
    return legacy ? [legacy] : [];
  }

  function ownRowChild(mount) {
    var kids = mount.children || [];
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].className && (' ' + kids[i].className + ' ').indexOf(' ibg-root ') !== -1) return kids[i];
    }
    return null;
  }

  /* Ensure an .ibg-root rail child exists in each mount target; return them all. */
  function ensureRowRoots() {
    var targets = rowMountTargets();
    var roots = [];
    for (var i = 0; i < targets.length; i++) {
      var mount = targets[i];
      var root = ownRowChild(mount);
      if (!root) {
        root = document.createElement('div');
        root.className = 'ibg-root';
        root.setAttribute('role', 'region');
        root.setAttribute('aria-label', 'Your free gifts');
        if (mount.firstChild) mount.insertBefore(root, mount.firstChild);
        else mount.appendChild(root);
      }
      bindRowRoot(root);
      roots.push(root);
    }
    return roots;
  }

  function ensureModalRoot() {
    if (ctrl.modalRoot && ctrl.modalRoot.isConnected) return ctrl.modalRoot;
    if (!hasDoc() || !document.body || !document.createElement) return null;
    var root = ctrl.modalRoot || document.getElementById(MODAL_ROOT_ID);
    if (!root) {
      root = document.createElement('div');
      root.id = MODAL_ROOT_ID;
      root.className = 'ibg-modal-root';
      root.hidden = true;
    }
    if (root.parentNode !== document.body) document.body.appendChild(root);
    ctrl.modalRoot = root;
    bindModalRoot(root);
    return root;
  }

  function bindRowRoot(root) {
    if (!root.addEventListener || root.__ibgBound) return;
    root.__ibgBound = true; /* per-element — each surface's rail binds its own opener */
    root.addEventListener('click', function (event) {
      var opener = closestAttrIn(root, event.target, 'data-ibg-open');
      if (opener) { openModal(); return; }
      var accept = closestAttrIn(root, event.target, 'data-ibg-deal-accept');
      if (accept) { onAcceptDeal(accept.getAttribute('data-ibg-deal-accept')); return; }
      /* Rail re-entry to the deal pop-up (review, 2026-09-02). */
      var dealOpen = closestAttrIn(root, event.target, 'data-ibg-deal-open');
      if (dealOpen) openDealModal(dealOpen.getAttribute('data-ibg-deal-open'));
    });
  }

  /* HC - reopen the deal pop-up from the rail (2026-09-02)
   * After the auto-open is dismissed the rail card is the way back: stage the
   * deal explicitly (the auto-open signature is unchanged, so onState would
   * not) and open. Only an OFFERED deal opens; a taken one has nothing to
   * show but its own cart row. */
  function armDealWatchdog(ruleId) {
    disarmDealWatchdog(ruleId);
    try {
      if (typeof setTimeout !== 'function') return;
      ctrl.dealTimers[ruleId] = setTimeout(function () { onDealAcceptTimeout(ruleId); }, DEAL_ACCEPT_TIMEOUT_MS);
    } catch (err) { /* no timers here (tests) — the promise path still settles */ }
  }
  function disarmDealWatchdog(ruleId) {
    if (ctrl.dealTimers[ruleId]) { try { clearTimeout(ctrl.dealTimers[ruleId]); } catch (err) { /* noop */ } }
    delete ctrl.dealTimers[ruleId];
  }
  /* A stalled accept flips to the retryable error state. The attempt token
   * is bumped FIRST, so if the original promise settles later it is stale and
   * ignored: it can neither clear a retry's "Adding…" nor disarm the retry's
   * watchdog (review 2026-09-02). The cart row and the next ibgift:state
   * carry the truth about whether the late add landed. */
  function onDealAcceptTimeout(ruleId) {
    delete ctrl.dealTimers[ruleId];
    if (ctrl.dealPending[ruleId] !== 'adding') return;
    ctrl.dealAttempt[ruleId] = (ctrl.dealAttempt[ruleId] || 0) + 1;
    ctrl.dealPending[ruleId] = 'error';
    render();
  }

  /* Stale per-rule accept errors die with the offer episode (review
   * 2026-09-02): a cart that dequalifies and later requalifies gets a fresh
   * card, not last time's "Try again". */
  function pruneDealPending() {
    for (var id in ctrl.dealPending) {
      if (!Object.prototype.hasOwnProperty.call(ctrl.dealPending, id)) continue;
      if (ctrl.dealPending[id] === 'error' && !offeredDealCard(ctrl.lastCards, id)) delete ctrl.dealPending[id];
    }
  }

  function openDealModal(ruleId) {
    if (!ruleId || !offeredDealCard(ctrl.lastCards, String(ruleId))) return false;
    if (commitActive()) return false;
    ctrl.dealStage = String(ruleId);
    return openModal();
  }

  /* HC - accept a deal (2026-09-01): the ONLY write this file makes for a
   * deal, and it is a call into the engine, never into the cart. (The drawer
   * flush below settles the shopper's OWN pending edits; it authors none.)
   * The card shows "Adding…" until the engine's promise settles; a refusal or
   * a failed add flips it to Try again. The engine also dispatches
   * ibgift:mutation-result, which the toast path ignores for deals (a paid
   * bundle is confirmed by its own cart row and the "Added" state here). */
  function onAcceptDeal(ruleId) {
    var engine = engineApi();
    if (!ruleId || !engine || typeof engine.acceptDeal !== 'function') return;
    if (ctrl.dealPending[ruleId] === 'adding') return;
    ctrl.dealPending[ruleId] = 'adding';
    ctrl.suppressToastVariants = ctrl.suppressToastVariants || {};
    /* Every accept is a numbered attempt. Only the CURRENT attempt's settle
     * may touch the card state: a timed-out attempt is invalidated by the
     * watchdog, and a retry starts a newer one, so a late completion of the
     * old promise is ignored instead of overwriting the retry. */
    var attempt = ctrl.dealAttempt[ruleId] = (ctrl.dealAttempt[ruleId] || 0) + 1;
    render();
    /* Watchdog (review 2026-09-02): a cart promise that never settles must not
     * leave both CTAs disabled for the rest of the session. */
    armDealWatchdog(ruleId);
    var settled = false;
    var settle = function (outcome) {
      if (settled) return;
      if (ctrl.dealAttempt[ruleId] !== attempt) return; /* stale: timed out or superseded by a retry */
      settled = true;
      disarmDealWatchdog(ruleId);
      var ok = !!(outcome && outcome.ok);
      /* HC - 2026-09-25: read the staged card while the accept still counts
       * as in flight. The engine dispatches the TAKEN state before this
       * promise settles, so once 'adding' is cleared a single-bundle deal is
       * no longer offered, stagedDealCard() returned null, and the modal
       * closed without its "Added" beat. */
      var staged = ok ? stagedDealCard() : null;
      if (ok) delete ctrl.dealPending[ruleId];
      else ctrl.dealPending[ruleId] = 'error';
      /* HC - deal pop-up "Added" beat (2026-09-02): when the accept came from
       * the modal, freeze the card in its added state for one beat, then
       * close — mirroring the picker's advanceAfterBeat. The engine's next
       * ibgift:state marks the deal taken, which would otherwise drop the
       * staged card (and the modal) the instant the add lands. */
      if (ok && ctrl.modalOpen && ctrl.dealStage === ruleId && !commitActive()) {
        if (staged) {
          clearDealBeat();
          ctrl.dealBeat = { ruleId: ruleId, card: staged, timer: null };
          ctrl.dealBeat.timer = setTimeout(function () {
            ctrl.dealBeat = null;
            ctrl.dealStage = null;
            if (!ctrl.modalOpen) { render(); return; }
            if (currentCard()) { render(); focusPanel(); } else { closeModal(true); }
          }, ADDED_BEAT_MS);
        }
      }
      render();
    };
    var accept = function () {
      try {
        var p = engine.acceptDeal(ruleId);
        if (p && typeof p.then === 'function') p.then(settle, function () { settle(null); });
        else settle(p);
      } catch (err) {
        settle(null);
      }
    };

    /* HC - flush the shopper's pending line edits first - 2026-09-01
     * acceptDeal re-qualifies against the engine's last authoritative cart. A
     * quantity typed a moment ago is still inside theme-cart.js's 300ms
     * line-write debounce, so the 48th tube that lit this card up may not be
     * in that cart yet and the accept would be refused as not-available.
     * flushPendingEdits() settles those writes and never rejects; when the
     * drawer is absent (cart page without v2, tests) we accept unflushed,
     * which is exactly today's behaviour. */
    var drawer = (typeof window !== 'undefined' && window.IBCartDrawer) || null;
    if (drawer && typeof drawer.flushPendingEdits === 'function') {
      try {
        Promise.resolve(drawer.flushPendingEdits()).then(accept, accept);
        return;
      } catch (err) { /* fall through — an unflushed accept beats no accept */ }
    }
    accept();
  }

  function bindModalRoot(root) {
    if (ctrl.modalBound || !root.addEventListener) return;
    ctrl.modalBound = true;

    root.addEventListener('click', function (event) {
      var closer = closestAttrIn(root, event.target, 'data-ibg-close');
      if (closer) { closeModal(true); return; }

      var commit = closestAttrIn(root, event.target, 'data-ibg-commit');
      if (commit) { onCommit(); return; }

      /* Deal pop-up CTA (2026-09-02): same accept path as the rail card. */
      var dealAccept = closestAttrIn(root, event.target, 'data-ibg-deal-accept');
      if (dealAccept) {
        if (dealAccept.getAttribute('aria-disabled') !== 'true') onAcceptDeal(dealAccept.getAttribute('data-ibg-deal-accept'));
        return;
      }

      var tile = closestAttrIn(root, event.target, 'data-ibg-tile');
      if (tile) onTile(tile.getAttribute('data-ibg-tile'));
    });

    root.addEventListener('keydown', function (event) {
      if (!ctrl.modalOpen) return;
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        closeModal(true);
        return;
      }
      if (event.key !== 'Tab') return;

      /* Containment-based focus trap (review F17): keep focus inside the panel
       * on EVERY Tab, not only when it sits on the first/last descendant. The
       * dialog container holds initial focus via tabindex=-1, so a Shift+Tab
       * from it used to escape; here any Tab that would leave the focusables
       * wraps to the opposite end. */
      var focusables = root.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) { event.preventDefault(); return; }
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;
      var within = false;
      for (var i = 0; i < focusables.length; i++) { if (focusables[i] === active) { within = true; break; } }

      if (!within) {
        /* focus is on the dialog container (or otherwise outside the tab ring) */
        event.preventDefault();
        var target = event.shiftKey ? last : first;
        if (target.focus) target.focus();
        return;
      }
      if (event.shiftKey && active === first) {
        event.preventDefault();
        if (last.focus) last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        if (first.focus) first.focus();
      }
    });
  }

  function closestAttrIn(root, el, attr) {
    while (el && el !== root) {
      if (el.getAttribute && el.getAttribute(attr) != null) return el;
      el = el.parentNode;
    }
    return null;
  }

  function commitActive() { return ctrl.commit.phase !== 'idle'; }

  /* The rule on stage: the one whose commit is resolving (held so the sync
   * ibgift:state that selectChoice dispatches can't swap it out mid-flight),
   * else the first still-pending rule. A fully-committed rule leaves the
   * engine's pending set on its own; a PARTIALLY-committed rule stays pending
   * with a reduced remaining count and is re-staged after the beat (F11). */
  function currentCard() {
    if (commitActive() && ctrl.commit.card) return ctrl.commit.card;
    var pending = choiceCardsOf(ctrl.lastCards);
    return pending.length ? pending[0] : null;
  }

  function currentSelection(card) {
    if (commitActive()) return ctrl.commit.variantIds;
    return ctrl.selByRule[card.ruleId] || [];
  }

  /* HC - deal pop-up staging (2026-09-02)
   * The deal the modal presents once the picker queue is empty: the "Added"
   * beat's frozen card first, else the STAGED rule while it is still offered.
   * Staging happens only when a deal's auto-open signature is new (onState),
   * so a dismissed offer never re-surfaces behind a later gift pick. */
  function offeredDealCard(cards, ruleId) {
    var deals = dealCardsOf(cards);
    for (var i = 0; i < deals.length; i++) {
      if (deals[i].ruleId === ruleId && deals[i].state === 'offered' && !deals[i].oos) return deals[i];
    }
    return null;
  }

  function stagedDealCard() {
    if (ctrl.dealBeat && ctrl.dealBeat.card) return ctrl.dealBeat.card;
    if (!ctrl.dealStage) return null;
    var offered = offeredDealCard(ctrl.lastCards, ctrl.dealStage);
    if (offered) { ctrl.dealStageCard = offered; return offered; }
    /* Accept in flight (review 2026-09-02): the engine reconciles and
     * dispatches the TAKEN state synchronously inside acceptDeal, before its
     * promise settles. Without this the staged card vanished on that dispatch,
     * the modal closed, and the "Added" beat never played. Hold the last
     * rendered card until settle() takes over. */
    if (ctrl.dealPending[ctrl.dealStage] === 'adding' && ctrl.dealStageCard &&
        ctrl.dealStageCard.ruleId === ctrl.dealStage) return ctrl.dealStageCard;
    return null;
  }

  function modalHasContent() {
    return pendingCount(ctrl.lastCards) > 0 || !!stagedDealCard();
  }

  function clearDealBeat() {
    if (ctrl.dealBeat && ctrl.dealBeat.timer) clearTimeout(ctrl.dealBeat.timer);
    ctrl.dealBeat = null;
  }

  /* A deal whose auto-open signature is NEW (first offer, tier switch, second
   * bundle earned) is staged for the modal. onState opens the modal if it is
   * closed; if the picker is already up, the deal follows the gift queue in
   * the same session. Nothing else ever stages a deal, so a dismissed offer
   * never re-surfaces behind a later gift pick. */
  function stageNewDeal(changedIds) {
    if (ctrl.dealStage) return;
    for (var i = 0; i < (changedIds || []).length; i++) {
      if (offeredDealCard(ctrl.lastCards, changedIds[i])) { ctrl.dealStage = changedIds[i]; return; }
    }
  }

  /* Register every live rule in session order so the "Offer N of M" marker has a
   * stable position that a partial commit (same rule) doesn't disturb (F11). */
  function ensureSessionRules() {
    var pending = choiceCardsOf(ctrl.lastCards);
    for (var i = 0; i < pending.length; i++) {
      if (ctrl.sessionRules.indexOf(pending[i].ruleId) === -1) ctrl.sessionRules.push(pending[i].ruleId);
    }
    if (commitActive() && ctrl.commit.ruleId && ctrl.sessionRules.indexOf(ctrl.commit.ruleId) === -1) {
      ctrl.sessionRules.push(ctrl.commit.ruleId);
    }
  }

  function buildView() {
    var card = currentCard();
    if (!card) {
      /* Picker queue empty: present the staged deal, if any (2026-09-02). */
      var deal = stagedDealCard();
      if (!deal) return null;
      return buildDealModalView({
        card: deal,
        pending: ctrl.dealPending[deal.ruleId] || null,
        added: !!(ctrl.dealBeat && ctrl.dealBeat.ruleId === deal.ruleId),
        isMobile: viewportW() < 720
      });
    }
    ensureSessionRules();
    var pos = ctrl.sessionRules.indexOf(card.ruleId);
    return buildModalView({
      card: card,
      selectedIds: currentSelection(card),
      markerPosition: (pos === -1 ? 0 : pos) + 1,
      markerTotal: Math.max(ctrl.sessionRules.length, 1),
      commitPhase: ctrl.commit.phase,
      isMobile: viewportW() < 720,
      viewportW: viewportW(),
      showValue: readConfig().showValue
    });
  }

  function openModal() {
    var root = ensureModalRoot();
    if (!root) return false;
    if (!ctrl.modalOpen) {
      ctrl.modalOpen = true;
      ctrl.lastFocus = (hasDoc() && document.activeElement) || null;
      /* Fresh session. */
      ctrl.selByRule = {};
      ctrl.sessionRules = [];
      resetCommit();
      lockBodyScroll();
    }
    render();
    focusPanel();
    return true;
  }

  function focusPanel() {
    if (!ctrl.modalRoot || !ctrl.modalRoot.querySelector) return;
    var dialog = ctrl.modalRoot.querySelector('.ibg-modal');
    if (dialog && dialog.focus) { try { dialog.focus(); } catch (err) { /* noop */ } }
  }

  function closeModal(restoreFocus) {
    if (!ctrl.modalOpen) return;
    ctrl.modalOpen = false;
    resetCommit();
    ctrl.selByRule = {};
    ctrl.sessionRules = [];
    ctrl.dealStage = null;
    ctrl.dealStageCard = null;
    clearDealBeat();
    unlockBodyScroll();
    render();
    if (restoreFocus && ctrl.lastFocus && ctrl.lastFocus.focus) {
      try { ctrl.lastFocus.focus(); } catch (err) { /* noop */ }
    }
    ctrl.lastFocus = null;
  }

  function resetCommit() {
    if (ctrl.commit.timer) { clearTimeout(ctrl.commit.timer); }
    if (ctrl.commit.beatTimer) { clearTimeout(ctrl.commit.beatTimer); }
    ctrl.commit = { phase: 'idle', ruleId: null, variantIds: [], card: null, timer: null, beatTimer: null };
  }

  /* Background scroll-lock with position preserve/restore (review F18). */
  function lockBodyScroll() {
    if (!hasDoc() || !document.body || ctrl.scrollLock) return;
    var y = (typeof window !== 'undefined' && (window.scrollY || window.pageYOffset)) || 0;
    var body = document.body;
    ctrl.scrollLock = {
      y: y,
      prev: { position: body.style.position, top: body.style.top, width: body.style.width, overflow: body.style.overflow }
    };
    body.style.position = 'fixed';
    body.style.top = -y + 'px';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
  }

  function unlockBodyScroll() {
    if (!hasDoc() || !document.body || !ctrl.scrollLock) return;
    var lock = ctrl.scrollLock;
    ctrl.scrollLock = null;
    var body = document.body;
    body.style.position = lock.prev.position || '';
    body.style.top = lock.prev.top || '';
    body.style.width = lock.prev.width || '';
    body.style.overflow = lock.prev.overflow || '';
    if (typeof window !== 'undefined' && window.scrollTo) {
      try { window.scrollTo(0, lock.y); } catch (e) { /* noop */ }
    }
  }

  /* Toggle a tile's selection UNIT. Caps with the REMAINING slots (view.maxPick)
   * and stores exactly the sanitized selection the view shows — one authority,
   * so the commit can never send a hidden pick (review F10). */
  function onTile(variantId) {
    if (commitActive()) return; /* selection is frozen while a commit resolves */
    var view = buildView();
    if (!view) return;
    var card = currentCard();
    var opt = optionOf(card, String(variantId));
    if (!opt || opt.oos) return; /* OOS tiles never enter the selection */
    ctrl.selByRule[card.ruleId] = toggleSelection(view.selectedIds, String(variantId), view.maxPick);
    render();
  }

  function optionOf(card, variantId) {
    for (var i = 0; i < (card.options || []).length; i++) {
      if (card.options[i].variantId === variantId) return card.options[i];
    }
    return null;
  }

  function rulePending(ruleId) {
    var pending = choiceCardsOf(ctrl.lastCards);
    for (var i = 0; i < pending.length; i++) {
      if (pending[i].ruleId === String(ruleId)) return true;
    }
    return false;
  }

  /* Has this rule vanished from the engine state entirely (dequalified mid-flight)
   * — no choice entry and no reward target? A satisfied/kept rule still appears
   * in choices, so this is true only for a genuine drop, not transient limbo. */
  function ruleGoneFromDetail(detail, ruleId) {
    ruleId = String(ruleId);
    var choices = (detail && detail.choices) || [];
    for (var i = 0; i < choices.length; i++) {
      if (choices[i] && String(choices[i].ruleId) === ruleId) return false;
    }
    var targets = (detail && detail.targets) || [];
    for (var t = 0; t < targets.length; t++) {
      if (targets[t] && String(targets[t].ruleId) === ruleId) return false;
    }
    return true;
  }

  /* CTA. In the error state the button is Retry; otherwise it commits the
   * sanitized selection and enters the async 'adding' state (review F2). */
  function onCommit() {
    if (ctrl.commit.phase === 'error') { retryCommit(); return; }
    if (commitActive()) return; /* adding / added — ignore */
    var view = buildView();
    if (!view || !view.ctaEnabled) return;
    var card = currentCard();
    var chosen = view.selectedIds.slice(); /* AUTHORITATIVE — exactly what is shown */
    if (!chosen.length) return;
    startCommit(card, card.ruleId, chosen);
  }

  function startCommit(card, ruleId, variantIds) {
    ctrl.commit = commitTransition(ctrl.commit, { type: 'commit', ruleId: ruleId, variantIds: variantIds });
    ctrl.commit.card = card;
    /* Suppress the reward-line toast — the in-modal beat is the confirmation.
     * The silent auto-add path keeps its toast (this set stays empty for it). */
    for (var i = 0; i < variantIds.length; i++) ctrl.suppressToastVariants[String(variantIds[i])] = true;
    ctrl.commit.timer = setTimeout(onCommitTimeout, COMMIT_TIMEOUT_MS);
    render(); /* "Adding…", CTA locked */
    /* selectChoice may dispatch ibgift:state synchronously; the commit phase
     * keeps the card on stage and blocks the auto-close through that nested pass. */
    commitSelection(engineApi(), ruleId, variantIds);
  }

  function retryCommit() {
    if (ctrl.commit.phase !== 'error') return;
    var ruleId = ctrl.commit.ruleId, variantIds = ctrl.commit.variantIds;
    ctrl.commit = commitTransition(ctrl.commit, { type: 'retry' });
    ctrl.commit.card = currentCard();
    for (var i = 0; i < variantIds.length; i++) ctrl.suppressToastVariants[String(variantIds[i])] = true;
    ctrl.commit.timer = setTimeout(onCommitTimeout, COMMIT_TIMEOUT_MS);
    render();
    /* Idempotent retry (review R2-4): these picks are already in engine storage
     * from the first attempt (kept across a transient failure), so re-issuing
     * selectChoice would double-append. Re-trigger a reconcile of the stored
     * picks instead — no append. Fall back to a re-send only if the engine can't
     * reconcile (older engine or no cached cart), where the engine's own prune
     * still converges duplicates. */
    var engine = engineApi();
    if (engine && typeof engine.reconcile === 'function' && ctrl.lastCart) {
      try { engine.reconcile(ctrl.lastCart); }
      catch (e) { commitSelection(engine, ruleId, variantIds); }
    } else {
      commitSelection(engine, ruleId, variantIds);
    }
  }

  function onCommitTimeout() {
    if (ctrl.commit.phase !== 'adding') return;
    var card = ctrl.commit.card;
    ctrl.commit = commitTransition(ctrl.commit, { type: 'timeout' });
    ctrl.commit.card = card;
    onCommitResolved();
  }

  /* The engine's mutation-result is the ONLY truthful confirmation that a gift
   * landed in the cart (review F2; team-lead 2026-07-21 — ibgift:state carries
   * DESIRED state, not cart state, so it can never confirm). Handled for both
   * 'adding' and 'error' (a transient failure the engine self-retried, or a
   * survivor auto-add, whose ok arrives after the failure). The reducer matches
   * OUR committed variant + failedRules; a survivor lands a DIFFERENT variant of
   * the same rule, so we also accept an ok carrying any option of the staged
   * card. */
  function onMutationResult(event) {
    if (ctrl.commit.phase !== 'adding' && ctrl.commit.phase !== 'error') return;
    var detail = (event && event.detail) || {};
    var card = ctrl.commit.card;
    var next = commitTransition(ctrl.commit, {
      type: 'result',
      ok: detail.ok,
      addedVariantIds: detail.addedVariantIds,
      failedRules: detail.failedRules
    });
    /* Survivor / mixed full-satisfaction: the reducer matches OUR committed
     * variants, but a single-survivor auto-add lands a DIFFERENT variant of the
     * same rule. Confirm only when the rule's added units FULLY cover the commit
     * (>= committed slot count) — never on a single hit of a multi-pick commit
     * (review R2-3). */
    if (next.phase === ctrl.commit.phase && detail.ok && card &&
        addedCountForRule(card, detail.addedVariantIds) >= ctrl.commit.variantIds.length &&
        ctrl.commit.variantIds.length > 0) {
      next = { phase: 'added', ruleId: ctrl.commit.ruleId, variantIds: ctrl.commit.variantIds };
    }
    if (next.phase === ctrl.commit.phase) return; /* a partial / unrelated result — keep waiting */
    if (ctrl.commit.timer) { clearTimeout(ctrl.commit.timer); ctrl.commit.timer = null; }
    next.card = card;
    ctrl.commit = next;
    onCommitResolved();
  }

  /* How many of the acknowledged-added variants belong to the staged rule — a
   * committed pick that landed, or a single-survivor the engine auto-added in its
   * place (team-lead 2026-07-21). Used to gate confirmation on FULL satisfaction. */
  function addedCountForRule(card, addedVariantIds) {
    var n = 0, av = addedVariantIds || [];
    for (var i = 0; i < av.length; i++) {
      if (optionOf(card, String(av[i]))) n++;
    }
    return n;
  }

  function onCommitResolved() {
    if (ctrl.commit.phase === 'added') {
      /* Confirmed: clear the working selection, play the beat, then advance. */
      delete ctrl.selByRule[ctrl.commit.ruleId];
      render();
      ctrl.commit.beatTimer = setTimeout(advanceAfterBeat, ADDED_BEAT_MS);
    } else if (ctrl.commit.phase === 'error') {
      /* Failed / timed out: no reward line will land, so release the toast
       * suppression, keep the rule staged, and offer Retry. Do NOT close, do
       * NOT mark the pending as seen. */
      for (var i = 0; i < ctrl.commit.variantIds.length; i++) {
        delete ctrl.suppressToastVariants[String(ctrl.commit.variantIds[i])];
      }
      render();
    }
  }

  function advanceAfterBeat() {
    resetCommit();
    if (!ctrl.modalOpen) { render(); return; } /* closed out from under the beat */
    /* currentCard() now reads the authoritative post-commit state: the next
     * queued rule, or the SAME rule with a reduced remaining count after a
     * partial commit (F11), or nothing left — in which case close. */
    if (currentCard()) {
      render();
      focusPanel();
    } else {
      closeModal(true);
    }
  }

  /* ------------------------------------------------------------------ *
   * Render pipeline
   * ------------------------------------------------------------------ */

  function onState(event) {
    try {
      var detail = (event && event.detail) || {};
      var model = buildModel(detail, ctrl.prevRuleIds);
      /* The engine's state is authoritative for the pending set. A commit in
       * flight holds its own card (ctrl.commit.card) through this pass, so a
       * fully-resolved rule simply leaves here and a partially-committed rule
       * stays with a reduced remaining — no separate optimistic-drop filter. */
      ctrl.lastCards = model.cards;
      ctrl.prevRuleIds = model.activeRuleIds;
      ctrl.lastTargets = detail.targets || [];

      /* Error watch (team-lead 2026-07-21). ibgift:state carries DESIRED state,
       * not cart state, so it can NEVER confirm a gift — the only state-driven
       * transitions out of 'error' are ones that don't claim success:
       *   (a) our rule re-presents as a PENDING choice card → leave the error
       *       into the refreshed picker (the dead option arrives greyed via oos).
       *   (b) our rule vanishes from the state entirely (dequalified mid-flight)
       *       → drop the commit so the normal pending-gone close / advance to the
       *       next queued rule applies (it was blocked by the 'error' phase).
       * Actual success is confirmed ONLY by a mutation-result ok (onMutationResult);
       * anything else keeps the Retry CTA. */
      if (ctrl.commit.phase === 'error') {
        if (rulePending(ctrl.commit.ruleId) || ruleGoneFromDetail(detail, ctrl.commit.ruleId)) {
          resetCommit();
        }
      }

      /* HC - modal auto-open-once (decisions §8 addendum 2) - 2026-07-20
       * A NEW pending signature (new rule, higher slot count, or a chosen option
       * going OOS) opens the modal once; the same pending state never re-opens
       * after dismissal. The seen-set persists for the session. */
      pruneDealPending(); /* deal pop-up (2026-09-02): stale errors, then staging */
      var auto = computeAutoOpen(ctrl.lastCards, seenGet());
      stageNewDeal(auto.changed);
      if (auto.shouldOpen && !ctrl.modalOpen) {
        if (openModal()) seenSet(auto.seen);
      } else {
        /* Persist even when nothing opens: computeAutoOpen PRUNES entries for
         * rules with no live pending, and that forgetting must reach storage —
         * otherwise a stale "seen" suppresses the re-offer when the shopper
         * re-earns the same slot count later (Noah live repro 2026-07-21). */
        seenSet(auto.seen);
      }
      /* Never yank the modal out from under a commit that is still resolving.
       * A staged deal (or its "Added" beat) keeps the modal up after the
       * picker queue drains (2026-09-02). */
      if (ctrl.modalOpen && !commitActive() && !modalHasContent()) closeModal(false);

      render();
      decorateLineItems();
    } catch (err) { /* fail open */ }
  }

  function render() {
    /* Re-entry rail (+ quiet lost notes) into every surface's mount (R2-10). */
    var rowRoots = ensureRowRoots();
    var rowHtml = renderEntryRowHTML(ctrl.lastCards);
    for (var r = 0; r < rowRoots.length; r++) {
      var rr = rowRoots[r];
      if (rr.__ibgHtml !== rowHtml) { rr.innerHTML = rowHtml; rr.__ibgHtml = rowHtml; }
      rr.hidden = !rowHtml;
    }

    /* Overlay modal. */
    var modalRoot = ensureModalRoot();
    if (!modalRoot) return;

    if (!ctrl.modalOpen) {
      if (ctrl.shellSig !== null) { modalRoot.innerHTML = ''; ctrl.shellSig = null; }
      modalRoot.hidden = true;
      unlockBodyScroll(); /* centralize release — every hide path frees the lock (R2-4) */
      return;
    }

    var view = buildView();
    if (!view) {
      /* Nothing left to show (queue drained mid-render): hide WITHOUT routing
       * through closeModal, so release the scroll lock here or the body strands
       * at position:fixed/overflow:hidden (review R2-4). */
      ctrl.modalOpen = false;
      resetCommit();
      modalRoot.innerHTML = '';
      ctrl.shellSig = null;
      modalRoot.hidden = true;
      unlockBodyScroll();
      return;
    }

    /* Rebuild the shell only when what it presents changes (the rule, its
     * options, the header copy); selection and commit-phase changes update in
     * place so transitions survive. */
    var sig = shellSignature(view);
    if (sig !== ctrl.shellSig) {
      /* Replacing innerHTML destroys the focused node, so focus falls to <body>
       * — and a body-focused keydown never reaches the panel-rooted focus trap,
       * stranding keyboard users outside the dialog (review R2-11). Restore focus
       * into the dialog whenever it was inside before the swap. */
      var hadFocus = !!(hasDoc() && document.activeElement && modalRoot.contains &&
        modalRoot.contains(document.activeElement));
      /* HC - 2026-09-25 (#176): the entrance animation is for OPENING the
       * modal. A swap while it is already on screen (next offer, a partial
       * re-stage, picker -> deal) skips it; otherwise the fresh backdrop and
       * panel restart at opacity 0 and the page flashes through. */
      toggleClass(modalRoot, 'is-settled', ctrl.shellSig !== null);
      modalRoot.innerHTML = renderModalView(view);
      ctrl.shellSig = sig;
      if (hadFocus) focusPanel();
    } else {
      applyState(view);
    }
    modalRoot.hidden = false;
  }

  /* What the shell PRESENTS: anything rendered here that applyState() does not
   * patch. The commit phase is deliberately absent (#176) — rebuilding on
   * idle -> adding -> added replaced the backdrop and panel mid-commit and
   * replayed their fade-in from opacity 0, flashing the page through twice. */
  function shellSignature(view) {
    if (view.kind === 'deal') return 'deal|' + view.ruleId + '|' + view.isMobile + '|' + view.card.amount;
    var ids = [];
    for (var i = 0; i < view.cards.length; i++) {
      ids.push(view.cards[i].variantId + (view.cards[i].oos ? 'x' : ''));
    }
    return view.ruleId + '|' + view.maxPick + '|' + view.reopened + '|' + view.isMobile + '|' +
      (view.marker.show ? view.marker.label : '') + '|' + ids.join(',');
  }

  /* In-place update — no innerHTML swap. Tile toggles and every commit phase
   * (idle/adding/added/error, picker and deal alike) land here, so the shell,
   * its scroll position and the focused CTA all survive (#176). */
  function applyState(view) {
    var root = ctrl.modalRoot;
    if (!root || !root.querySelector) return;

    var panel = root.querySelector('.ibg-modal-panel');
    if (panel) {
      toggleClass(panel, 'is-adding', view.adding);
      toggleClass(panel, 'is-added', view.added);
      toggleClass(panel, 'is-error', view.errored);
    }

    var summary = root.querySelector('.ibg-summary');
    if (summary) summary.className = 'ibg-summary ibg-summary--' + view.summaryTone;
    setText(root.querySelector('.ibg-summary-top'), view.summaryTop);
    setText(root.querySelector('.ibg-summary-sub'), view.summarySub);

    var cta = root.querySelector('.ibg-cta');
    if (cta) {
      toggleClass(cta, 'is-disabled', !view.ctaEnabled);
      if (view.ctaEnabled) cta.removeAttribute('aria-disabled');
      else cta.setAttribute('aria-disabled', 'true');
      if (view.busy) cta.setAttribute('aria-busy', 'true');
      else cta.removeAttribute('aria-busy');
    }
    setText(root.querySelector('.ibg-cta-label'), view.ctaLabel);

    if (view.kind === 'deal') return; /* the rest is picker-only */

    if (panel) toggleClass(panel, 'is-atcap', view.atCap);

    var fill = root.querySelector('.ibg-progress-fill');
    if (fill) fill.style.width = view.pct + '%';
    setText(root.querySelector('.ibg-progress-label'), view.count + ' of ' + view.maxPick + ' chosen');
    setText(root.querySelector('.ibg-single-prompt'), view.singlePrompt);

    var status = root.querySelector('.ibg-avail-status');
    if (status) {
      status.className = 'ibg-avail-status ibg-avail-status--' + view.availTone;
      status.textContent = view.availStatus;
    }

    var byVariant = {};
    for (var i = 0; i < view.cards.length; i++) byVariant[view.cards[i].variantId] = view.cards[i];
    var tiles = root.querySelectorAll('.ibg-card[data-ibg-tile]');
    for (var t = 0; t < tiles.length; t++) {
      var el = tiles[t];
      var c = byVariant[el.getAttribute('data-ibg-tile')];
      if (!c) continue;
      toggleClass(el, 'is-selected', c.selected);
      toggleClass(el, 'is-dup', c.count >= 2);
      toggleClass(el, 'is-dim', c.dim);
      var countEl = el.querySelector('.ibg-card-count');
      if (countEl) countEl.textContent = c.count >= 2 ? '×' + c.count : '';
      if (el.setAttribute) el.setAttribute('aria-pressed', c.selected ? 'true' : 'false');
    }
  }

  function toggleClass(el, cls, on) {
    if (!el) return;
    if (el.classList && el.classList.toggle) { el.classList.toggle(cls, !!on); return; }
    var has = (' ' + el.className + ' ').indexOf(' ' + cls + ' ') !== -1;
    if (on && !has) el.className += ' ' + cls;
    else if (!on && has) el.className = (' ' + el.className + ' ').replace(' ' + cls + ' ', ' ').replace(/^\s+|\s+$/g, '');
  }

  /* Skips unchanged text: the footer summary is a live region, and every
   * engine/cart pass re-renders through here. */
  function setText(el, text) { if (el && el.textContent !== text) el.textContent = text; }

  /* The set of reward cart-line KEYS from a cart:updated payload — a line is a
   * reward iff it carries the engine's `_ib_reward` property. Keyed by the
   * Shopify line key so identity is authoritative (review F14): a paid line that
   * happens to share a variant with a reward has a DIFFERENT key + no property,
   * and is never badged. */
  function rewardKeySet(cart) {
    var set = {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (line && line.properties && line.properties._ib_reward && line.key != null) {
        set[String(line.key)] = true;
      }
    }
    return set;
  }

  /* Badge reward line items by their authoritative cart-line key (review F14).
   * Runs on every cart:updated (fresh keys) and on re-renders (cached keys),
   * since the drawer/cart DOM is re-scraped on each mutation. */
  /* Deal lines (2026-09-01): badged "Intro deal" the same way, from _ib_deal. */
  function dealKeySet(cart) {
    var set = {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (line && line.properties && line.properties._ib_deal && line.key != null) {
        set[String(line.key)] = true;
      }
    }
    return set;
  }

  function decorateLineItems() {
    if (!hasDoc() || !document.querySelectorAll) return;
    var keys = ctrl.lastRewardKeys || {};
    var dealKeys = ctrl.lastDealKeys || {};

    var items = document.querySelectorAll('.cart__item');
    for (var j = 0; j < items.length; j++) {
      var item = items[j];
      var key = item.getAttribute && item.getAttribute('data-key');
      var isReward = key != null && keys[String(key)] === true;
      var isDeal = !isReward && key != null && dealKeys[String(key)] === true;
      var alreadyFlagged = item.className.indexOf('ibg-is-reward') !== -1;
      var alreadyDeal = item.className.indexOf('ibg-is-deal') !== -1;

      if (isReward && !alreadyFlagged) {
        item.className += ' ibg-is-reward';
        var titleWrap = item.querySelector('.cart__item-title');
        if (titleWrap && titleWrap.insertAdjacentHTML) {
          titleWrap.insertAdjacentHTML('afterbegin',
            '<span class="ibg-line-flag">' + SVG.check + 'Free gift</span>');
        }
      } else if (!isReward && alreadyFlagged) {
        item.className = item.className.replace(/\s*ibg-is-reward/g, '');
        var flag = item.querySelector('.ibg-line-flag');
        if (flag && flag.parentNode) flag.parentNode.removeChild(flag);
      }

      if (isDeal && !alreadyDeal) {
        item.className += ' ibg-is-deal';
        var dealTitle = item.querySelector('.cart__item-title');
        if (dealTitle && dealTitle.insertAdjacentHTML) {
          dealTitle.insertAdjacentHTML('afterbegin',
            '<span class="ibg-line-flag ibg-line-flag--deal">' + SVG.check + 'Intro deal</span>');
        }
      } else if (!isDeal && alreadyDeal) {
        item.className = item.className.replace(/\s*ibg-is-deal/g, '');
        var dealFlag = item.querySelector('.ibg-line-flag--deal');
        if (dealFlag && dealFlag.parentNode) dealFlag.parentNode.removeChild(dealFlag);
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * HC - Free-gift toast, theme pattern (Noah, 2026-07-20) - 2026-07-21
   * A brief toast when an engine-inserted reward line NEWLY lands in the cart —
   * deliberately the SAME toast family shoppers already see on collection/promo
   * adds. It fires ONLY for silent auto-adds (single-option rules). A gift the
   * shopper picked in the modal is confirmed by the in-modal "added" beat, so
   * its variant is suppressed here (see ctrl.suppressToastVariants).
   * ------------------------------------------------------------------ */

  var TOAST_VISIBLE_MS = 2500; /* match collection-atc showToastElement */
  var TOAST_EXIT_MS = 300;
  var toastState = { baseline: null, el: null, timer: null };

  function rewardLineKeys(cart) {
    var keys = {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (!line || !line.properties || !line.properties._ib_reward) continue;
      keys[String(line.key || (line.variant_id + '|' + line.properties._ib_reward))] = line;
    }
    return keys;
  }

  function ensureToastContainer() {
    var container = document.body.querySelector && document.body.querySelector(':scope > .cg-toast-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'cg-toast-container';
      if (container.setAttribute) container.setAttribute('popover', 'manual');
      document.body.appendChild(container);
    }
    if (container.setAttribute) {
      if (!container.getAttribute || !container.getAttribute('role')) container.setAttribute('role', 'status');
      if (!container.getAttribute || !container.getAttribute('aria-live')) container.setAttribute('aria-live', 'polite');
      if (!container.getAttribute || !container.getAttribute('aria-atomic')) container.setAttribute('aria-atomic', 'true');
    }
    try {
      if (container.matches && !container.matches(':popover-open')) container.showPopover();
    } catch (e) { /* popover unsupported — fixed positioning still applies */ }
    return container;
  }

  function dismissGiftToast() {
    if (toastState.timer) { clearTimeout(toastState.timer); toastState.timer = null; }
    var el = toastState.el;
    toastState.el = null;
    if (!el || !el.parentNode) return;
    var container = el.parentNode;
    if (el.classList && el.classList.remove) el.classList.remove('is-visible');
    else el.className = el.className.replace(/\s*is-visible/g, '');
    setTimeout(function () {
      if (el.parentNode) el.parentNode.removeChild(el);
      if (container && container.className && container.className.indexOf('cg-toast-container') !== -1 &&
          !container.hasChildNodes()) {
        try { container.hidePopover(); } catch (e) {}
        if (container.parentNode) container.parentNode.removeChild(container);
      }
    }, TOAST_EXIT_MS);
  }

  function showGiftToast(meta) {
    if (!hasDoc() || !document.body || !document.createElement) return;
    meta = meta || {};
    dismissGiftToast();

    var container = ensureToastContainer();
    var el = document.createElement('div');
    el.className = 'cg-toast';

    var media = '';
    var image = safeImageUrl(meta.image);
    if (image) {
      media = '<img class="cg-toast__image" src="' + esc(image) + '" alt="">';
    }
    el.innerHTML =
      media +
      '<div class="cg-toast__text">' +
        '<div class="cg-toast__line">' + esc(meta.label || 'Free gift added') + '</div>' +
        '<div class="cg-toast__line cg-toast__line--meta">' + esc(meta.title || '') + '</div>' +
      '</div>';

    container.appendChild(el);
    toastState.el = el;

    var reveal = function () {
      if (toastState.el === el) {
        if (el.classList && el.classList.add) el.classList.add('is-visible');
        else el.className += ' is-visible';
      }
    };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(reveal);
    else setTimeout(reveal, 16);

    toastState.timer = setTimeout(dismissGiftToast, TOAST_VISIBLE_MS);
  }

  function trackGiftTransitions(detail) {
    detail = detail || {};
    if (detail.optimistic || detail.countOnly || detail.lightweight) return false;
    var cart = detail.cart || detail.cartState;
    if (!cart || Object.prototype.toString.call(cart.items) !== '[object Array]') return false;
    var current = rewardLineKeys(cart);
    var baseline = toastState.baseline;
    toastState.baseline = current;
    if (baseline === null) return false; /* first observation = page load */
    for (var key in current) {
      if (!Object.prototype.hasOwnProperty.call(current, key)) continue;
      if (baseline[key]) continue;
      var line = current[key] || {};
      /* A gift the shopper chose in the modal is confirmed in-modal — don't
       * double-confirm with a toast. Consume the suppression once. */
      var vid = line.variant_id != null ? String(line.variant_id) : '';
      if (vid && ctrl.suppressToastVariants[vid]) {
        delete ctrl.suppressToastVariants[vid];
        continue;
      }
      showGiftToast({
        title: line.product_title || line.title || '',
        image: line.image || (line.featured_image && line.featured_image.url) || ''
      });
      return true;
    }
    return false;
  }

  /* ------------------------------------------------------------------ *
   * Bootstrap
   * ------------------------------------------------------------------ */

  function init() {
    if (!hasDoc()) return;
    document.addEventListener('ibgift:state', onState);
    /* The engine's reconciler reports each cart mutation's outcome; the async
     * commit flow keys its success/failure beat off this event (review F2). */
    document.addEventListener('ibgift:mutation-result', onMutationResult);
    document.addEventListener('ibgift:choices-updated', function () {
      /* The engine follows a selection with a fresh ibgift:state; this is a
       * cheap re-decorate hook in case a cart swap raced ahead of state. */
      decorateLineItems();
    });
    document.addEventListener('cart:updated', function (event) {
      var detail = (event && event.detail) || {};
      var cart = detail.cart || detail.cartState;
      /* Only an AUTHORITATIVE cart snapshot updates the reward-key set; a
       * count-only / optimistic / cartless event carries no line data and must
       * NOT wipe the badges the last real snapshot established (review R2-12). */
      var authoritative = !detail.optimistic && !detail.countOnly && !detail.lightweight &&
        cart && Object.prototype.toString.call(cart.items) === '[object Array]';
      if (authoritative) {
        ctrl.lastCart = cart;                 /* cached for an idempotent retry (R2-4) */
        ctrl.lastRewardKeys = rewardKeySet(cart);
        ctrl.lastDealKeys = dealKeySet(cart);
      }
      decorateLineItems();                    /* re-applies cached keys; safe on any event */
      trackGiftTransitions(detail);
    });
    /* HC - deal removed (2026-09-01): the engine took an accepted bundle back
     * out because the cart no longer qualifies (Discount Kit stopped pricing
     * it). A paid line vanishing without a word is worse than a toast. */
    document.addEventListener('ibgift:deal-removed', function (event) {
      try {
        var d = (event && event.detail) || {};
        var why;
        if (d.reason === 'dequalified' && d.min) why = 'your cart no longer has ' + d.min + ' qualifying items';
        else if (d.reason === 'excess') why = 'the extra bundle was not earned';
        else if (d.reason === 'unpriced') why = 'the bundle price was not applied to your cart';
        else why = 'that offer has ended';
        showGiftToast({ title: (d.name ? d.name + ' removed — ' : 'Intro deal removed — ') + why, image: '', label: 'Intro deal' });
      } catch (err) { /* fail open */ }
    });
    /* HC - cart drawer v2 cold open - 2026-08-20
     * The v2 drawer is display:none while closed, so every visibility-gated
     * mount decision here (resolveMount, and the legacy .cart__scrollable /
     * .cart__items fallbacks it walks when a theme has no explicit anchor)
     * skips it. A picker row earned BEFORE the first open then has nowhere to
     * land and only appears at the next cart mutation. Repaint the moment the
     * sheet becomes visible, through exactly the path the gift/cart events
     * use. Idempotent: render() diffs its own HTML and decorateLineItems()
     * re-applies cached keys, so an already-mounted row is left alone. */
    document.addEventListener('CartDrawer:open', function () {
      try { render(); decorateLineItems(); } catch (err) { /* fail open */ }
    });
  }

  var api = {
    version: '0.2.0',
    _test: {
      /* HC - 2026-08-20: exposed so the CartDrawer:open hook above can be
       * wired against a test DOM; init() is otherwise self-starting. */
      init: init,
      resolveMount: resolveMount,
      buildModel: buildModel,
      buildCard: buildCard,
      buildDealCard: buildDealCard,
      dealCardHtml: dealCardHtml,
      dealCardsOf: dealCardsOf,
      dealKeySet: dealKeySet,
      onAcceptDeal: onAcceptDeal,
      normOption: normOption,
      toggleSelection: toggleSelection,
      commitTransition: commitTransition,
      buildModalView: buildModalView,
      renderModalView: renderModalView,
      renderModalHTML: renderModalHTML,
      /* HC - deal pop-up (2026-09-02): the view pair plus the staging
       * helpers, and the controller state for staging tests. */
      buildDealModalView: buildDealModalView,
      renderDealModalView: renderDealModalView,
      offeredDealCard: offeredDealCard,
      openDealModal: openDealModal,
      onDealAcceptTimeout: onDealAcceptTimeout,
      pruneDealPending: pruneDealPending,
      stagedDealCard: stagedDealCard,
      modalHasContent: modalHasContent,
      buildView: buildView,
      ctrl: function () { return ctrl; },
      /* HC - 2026-09-25 (#176): the modal pipeline, so a test DOM can pin
       * which transitions rebuild the shell and which update it in place. */
      render: render,
      openModal: openModal,
      onTile: onTile,
      onCommit: onCommit,
      onMutationResult: onMutationResult,
      shellSignature: shellSignature,
      renderEntryRowHTML: renderEntryRowHTML,
      pendingSignature: pendingSignature,
      computeAutoOpen: computeAutoOpen,
      pendingCount: pendingCount,
      choiceCardsOf: choiceCardsOf,
      commitSelection: commitSelection,
      trackGiftTransitions: trackGiftTransitions,
      rewardLineKeys: rewardLineKeys,
      rewardKeySet: rewardKeySet,
      showGiftToast: showGiftToast,
      dismissGiftToast: dismissGiftToast,
      toastState: toastState,
      suppressToastVariants: function (vid) { ctrl.suppressToastVariants[String(vid)] = true; },
      esc: esc,
      safeUrl: safeUrl,
      safeImageUrl: safeImageUrl,
      SVG: SVG,
      GRADIENTS: GRADIENTS
    }
  };

  if (typeof window !== 'undefined') {
    window.IBGiftChoiceUI = api;
    if (!window.IB_GIFT_CHOICE_UI_MANUAL) {
      if (hasDoc() && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
      } else {
        init();
      }
    }
  }
})();
